import React, {
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { PanierContext } from "../../contexts/PanierContext";
import Button from "../UI/Button";

/**
 * ============================================================================
 * Navigation.jsx
 * ============================================================================
 *
 * RÔLE
 * ----------------------------------------------------------------------------
 * Gère uniquement :
 * - la navigation principale desktop
 * - le drawer mobile
 * - la bottom navigation mobile
 */

const Navigation = React.memo(
  ({
    t,
    vueActuelle,
    resetVues,
    setVueActuelle,
    setCategorieActive,
    authStatus,
    userProfil,
    isAdmin,
    isCollaborator,
    onLogout,
  }) => {
    const { panier } = useContext(PanierContext);

    const isLoggedIn = authStatus === "connected";

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showFloatingNav, setShowFloatingNav] = useState(true);

    const hideTimerRef = useRef(null);

    const totalItems = useMemo(() => {
      if (!Array.isArray(panier) || panier.length === 0) return 0;

      const keys = new Set(
        panier.map(
          (it) => it?.cartKey || `${it?.id}:${Number(it?.grams || 0) || 0}`
        )
      );

      return keys.size;
    }, [panier]);

    const goTo = useCallback(
      (vue) => {
        resetVues?.();
        setVueActuelle(vue);
        setCategorieActive?.(null);
        setIsMobileMenuOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
      [resetVues, setVueActuelle, setCategorieActive]
    );

    const isTabActive = useCallback(
      (tab) => vueActuelle === tab,
      [vueActuelle]
    );

    const revealFloatingNav = useCallback(() => {
      setShowFloatingNav(true);

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }

      hideTimerRef.current = setTimeout(() => {
        setShowFloatingNav(false);
      }, 2000);
    }, []);

    useEffect(() => {
      const isMobile = () => window.innerWidth <= 768;
      if (!isMobile()) return undefined;

      const onTouchStart = () => revealFloatingNav();
      const onTouchMove = () => revealFloatingNav();
      const onScroll = () => revealFloatingNav();
      const onPointerDown = () => revealFloatingNav();

      window.addEventListener("touchstart", onTouchStart, { passive: true });
      window.addEventListener("touchmove", onTouchMove, { passive: true });
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("pointerdown", onPointerDown, { passive: true });

      revealFloatingNav();

      return () => {
        window.removeEventListener("touchstart", onTouchStart);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("pointerdown", onPointerDown);

        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
      };
    }, [revealFloatingNav]);

    if (authStatus === "loading") return null;

    return (
      <>
        <nav className="main-nav">
          <div className="nav-container">
            <span
              className="nav-logo"
              onClick={() => goTo("accueil")}
              style={{ cursor: "pointer" }}
              aria-label="Marché Deluxe"
              title="Marché Deluxe"
            >
              <img
                className="nav-logo-svg"
                src="/logo-marche-deluxe.svg"
                alt="Marché Deluxe"
              />
            </span>

            <div className="nav-links">
              {isAdmin && (
                <a
                  href="#"
                  className={
                    isTabActive("admin") ? "active admin-link" : "admin-link"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    goTo("admin");
                  }}
                  style={{ color: "var(--gold)", fontWeight: "bold" }}
                >
                  ⚙️ {t?.adminInventory || "INVENTAIRE"}
                </a>
              )}
              {isAdmin && (
                <a
                  href="#"
                  className={
                    isTabActive("admin-milk-types")
                      ? "active admin-link"
                      : "admin-link"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    goTo("admin-milk-types");
                  }}
                  style={{ color: "var(--gold)", fontWeight: "bold" }}
                >
                  🥛 {t?.adminMilkTypes || "TYPES DE LAIT"}
                </a>
              )}

              {(isAdmin || isCollaborator) && (
                <a
                  href="#"
                  className={
                    isTabActive("dashboard-magasin")
                      ? "active collab-link"
                      : "collab-link"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    goTo("dashboard-magasin");
                  }}
                  style={{ color: "var(--gold)" }}
                >
                  📋 {t?.dashboardOrders || "COMMANDES"}
                </a>
              )}

              <a
                href="#"
                className={isTabActive("actions") ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  goTo("actions");
                }}
              >
                {t?.actions || "Actions"}
              </a>

              <a
                href="#"
                className={isTabActive("produits") ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  goTo("produits");
                }}
              >
                {t?.products || "Produits"}
              </a>

              <a
                href="#"
                className={isTabActive("magasin") ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  goTo("magasin");
                }}
              >
                {t?.onlineShop || "Magasin en ligne"}
              </a>

              <button
                type="button"
                className="nav-cart-icon"
                onClick={() => goTo("panier")}
                aria-label={t?.cart || "Panier"}
                title={t?.cart || "Panier"}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  className="nav-cart-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 1280 1049"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <g
                    transform="translate(0,1049) scale(0.1,-0.1)"
                    stroke="none"
                  >
                    <path
                      d="M3080 12790 c-377 -29 -677 -109 -865 -232 -107 -70 -344 -307 -405
-406 -25 -40 -57 -89 -72 -109 -15 -19 -30 -52 -33 -72 -4 -20 -22 -80 -40
-133 -60 -172 -77 -344 -100 -1038 -18 -564 -24 -689 -40 -862 -9 -100 -10
-163 -3 -230 7 -63 7 -283 -1 -683 -6 -324 -11 -654 -11 -732 l0 -142 78 -36
c139 -65 258 -115 283 -117 21 -3 30 9 79 109 31 62 64 121 73 131 21 23 21
29 3 48 -27 26 -29 127 -17 759 6 347 16 869 21 1160 12 640 27 1169 41 1443
11 232 30 314 104 468 74 153 191 247 355 284 144 32 504 23 925 -25 270 -31
399 -39 1085 -70 311 -14 624 -30 695 -35 314 -24 416 -31 680 -45 371 -20
525 -32 735 -55 96 -10 220 -21 275 -25 230 -13 495 -26 905 -45 572 -27 1006
-55 1225 -80 44 -5 215 -14 380 -20 330 -11 716 -36 810 -52 33 -5 75 -18 93
-29 39 -23 197 -79 221 -79 37 0 142 -63 210 -126 76 -71 187 -232 227 -332
30 -76 51 -182 63 -327 13 -148 14 -828 1 -1105 -5 -113 -16 -432 -25 -710
-25 -828 -35 -1064 -45 -1074 -23 -23 -311 -33 -352 -12 -15 8 -42 17 -60 21
-18 4 -43 16 -55 28 -31 27 -53 9 -53 -45 0 -51 28 -104 95 -179 66 -74 122
-183 135 -263 5 -32 16 -66 25 -76 14 -15 14 -19 1 -29 -36 -30 -117 -42 -351
-53 -328 -15 -324 -16 -352 16 -22 26 -26 38 -57 176 -9 41 -32 103 -50 138
-28 50 -31 62 -17 62 9 0 88 -7 176 -15 205 -19 395 -20 419 -2 16 12 16 14
-1 29 -41 38 -211 64 -492 76 -153 7 -175 10 -187 27 -17 23 -18 71 -1 77 6 3
135 -13 285 -34 151 -22 280 -37 288 -34 8 3 14 19 14 41 0 54 30 48 -499 95
-118 10 -219 24 -225 30 -5 5 -52 10 -106 10 -91 0 -98 -1 -119 -26 -21 -25
-25 -25 -109 -20 -48 3 -166 10 -262 16 -237 14 -325 26 -406 54 -38 13 -107
29 -154 35 -72 10 -92 10 -129 -3 -80 -29 -508 -18 -636 16 -22 5 -49 20 -61
33 -28 31 -96 41 -166 25 -41 -9 -78 -10 -156 -1 -57 6 -213 11 -347 11 -234
0 -245 1 -255 20 -8 16 -21 20 -60 20 -43 0 -51 4 -70 30 -29 40 -53 38 -101
-11 -31 -32 -45 -40 -62 -35 -122 34 -607 28 -550 -7 5 -3 102 -11 214 -17
244 -13 385 -32 438 -60 91 -46 64 -60 -119 -60 -154 0 -352 20 -458 47 -61
15 -70 22 -152 105 -48 48 -95 88 -104 88 -9 0 -29 -9 -44 -19 -37 -26 -140
-32 -378 -21 -206 9 -225 13 -278 69 -27 28 -36 31 -89 31 -69 0 -130 -16
-152 -40 -13 -15 -11 -19 29 -47 54 -38 140 -158 200 -278 54 -110 74 -168 82
-242 l7 -56 -29 7 c-16 4 -87 11 -159 16 -222 15 -443 53 -493 86 -45 30 -139
281 -114 306 9 9 54 8 189 -3 268 -21 331 -15 373 36 12 15 12 19 -4 30 -9 7
-41 16 -69 20 -79 10 -86 14 -102 51 -10 24 -26 40 -52 51 -56 23 -303 63
-415 67 -60 2 -103 8 -113 16 -8 7 -25 10 -37 7 -12 -4 -47 5 -82 20 -66 27
-113 27 -181 -1 -40 -17 -45 -31 -13 -39 58 -16 -17 -21 -300 -22 -321 -1
-392 4 -454 36 -23 12 -58 19 -94 19 -82 0 -252 -37 -299 -66 -114 -69 -394
-190 -485 -209 -77 -17 -105 -58 -113 -165 -3 -41 -17 -120 -31 -175 l-24
-100 76 -83 c43 -46 96 -105 119 -130 31 -36 44 -44 53 -35 19 18 3 65 -41
124 -21 29 -39 62 -39 74 0 29 61 108 103 134 26 16 51 21 99 21 78 0 85 -6
128 -110 53 -129 51 -173 -12 -203 -32 -16 -34 -15 -54 10 -27 34 -52 26 -104
-36 -22 -27 -52 -52 -66 -55 -15 -4 -33 -14 -41 -21 -15 -15 -97 -21 -127 -9
-9 3 -24 25 -35 48 -24 53 -97 136 -121 136 -11 0 -31 -20 -50 -50 -43 -66
-112 -208 -120 -245 -4 -22 -11 -29 -26 -27 -12 1 -36 -14 -64 -43 -52 -53
-75 -59 -66 -17 4 15 40 97 81 183 41 85 75 162 75 170 0 8 -14 -1 -30 -21
l-29 -35 -1 26 c0 27 15 45 53 64 12 6 25 25 31 42 13 46 -10 75 -81 103 -95
38 -187 86 -208 110 -11 12 -24 36 -30 53 -8 24 -17 33 -38 35 -67 8 -252 -70
-330 -138 -53 -47 -57 -63 -23 -85 34 -22 67 -106 75 -189 7 -77 2 -120 -22
-208 -18 -64 -13 -98 13 -98 19 0 23 9 35 70 4 19 18 58 31 85 22 48 23 53 9
105 -16 59 -20 200 -7 200 19 0 123 -63 132 -80 25 -46 5 -153 -36 -191 -14
-13 -47 -60 -74 -104 -76 -126 -89 -135 -217 -151 -171 -21 -174 -20 -132 51
45 76 3 81 -218 30 -20 -5 -23 -2 -23 29 0 18 8 43 18 55 l17 20 115 -19 c63
-11 123 -23 133 -26 25 -8 39 -41 32 -75 -13 -58 47 -23 84 49 62 122 87 326
47 387 -18 27 -19 28 -33 9 -10 -13 -13 -33 -9 -62 8 -59 -11 -52 -46 18 -14
30 -34 60 -45 68 -25 19 -82 10 -173 -28 -124 -52 -290 -98 -425 -119 -105
-16 -142 -29 -180 -63 -48 -41 -119 -188 -140 -285 -9 -43 -26 -114 -38 -158
-18 -64 -22 -107 -22 -215 0 -153 10 -185 84 -270 l39 -45 35 19 c43 22 60 17
87 -26 24 -40 26 -74 4 -86 -14 -8 -9 -14 30 -37 55 -32 69 -53 53 -80 -17
-29 -13 -229 8 -365 40 -270 96 -405 139 -338 8 13 7 26 -3 50 -57 142 -112
592 -81 673 9 24 14 26 62 23 l53 -3 -3 -80 c-7 -191 68 -615 109 -615 15 0
10 67 -17 256 -14 98 -28 233 -31 299 -7 154 -9 152 117 125 51 -11 96 -23 99
-26 3 -3 5 -78 3 -167 -3 -166 16 -367 40 -424 11 -26 12 -23 19 47 5 41 5
179 2 306 l-6 232 74 13 c108 18 166 15 229 -13 30 -14 67 -28 82 -31 15 -4
50 -27 78 -52 28 -25 67 -52 86 -60 67 -28 195 -45 421 -55 126 -5 334 -14
463 -20 129 -6 269 -15 311 -21 l76 -11 -1 -306 c-1 -217 3 -323 12 -361 16
-69 4 -86 -35 -49 -16 14 -41 29 -58 33 -16 4 -142 11 -280 15 -209 6 -256 11
-284 25 -30 16 -55 17 -225 12 -195 -6 -276 -2 -276 15 0 5 20 17 44 28 24 10
47 23 50 29 13 20 -43 42 -135 52 -115 13 -143 23 -159 57 -7 15 -17 95 -22
177 -15 274 -26 410 -33 428 -10 24 -24 21 -35 -9 -13 -33 -13 -568 0 -656 5
-37 7 -74 4 -82 -9 -23 -92 -57 -179 -73 -109 -21 -431 -21 -542 0 -162 30
-167 39 -188 303 -8 104 -24 272 -35 373 -11 101 -20 202 -20 225 0 53 -13 75
-37 60 -9 -6 -21 -24 -25 -40 -16 -55 18 -503 48 -633 l14 -64 -25 -27 c-17
-18 -29 -47 -37 -87 -14 -78 -6 -113 27 -113 30 0 29 5 10 -67 -21 -79 -30
-380 -17 -513 9 -79 20 -126 46 -190 19 -47 39 -110 44 -140 6 -30 18 -75 27
-100 31 -84 45 -144 65 -295 11 -82 29 -193 39 -245 11 -52 22 -116 26 -142 7
-54 16 -64 45 -48 25 13 25 32 4 228 -8 81 -19 191 -24 245 -7 80 -12 103 -30
122 -14 15 -20 30 -16 43 13 41 79 31 96 -14 16 -40 72 -544 64 -576 -3 -16
-17 -49 -31 -73 -13 -25 -23 -55 -21 -67 3 -21 9 -23 59 -25 31 -1 92 -12 135
-25 71 -21 100 -23 279 -24 110 0 208 -1 217 -2 10 -1 24 -16 33 -32 13 -25
21 -30 54 -30 69 0 79 -26 111 -299 25 -211 45 -306 72 -333 39 -39 43 -11 25
169 -19 180 -22 310 -9 331 12 19 53 1 85 -38 15 -17 35 -59 46 -94 17 -56 24
-106 47 -321 6 -66 16 -82 35 -59 19 23 24 189 10 313 -13 113 -3 144 45 149
40 4 81 -29 93 -76 6 -20 17 -97 26 -171 15 -132 29 -181 51 -188 22 -7 24 31
13 207 -9 140 -8 182 1 201 l13 23 17 -22 c10 -12 21 -46 25 -74 24 -193 52
-308 74 -308 18 0 24 71 18 224 -5 141 1 214 15 191 3 -6 18 -55 33 -110 25
-95 47 -241 61 -405 9 -101 2 -108 -102 -90 -56 10 -141 11 -337 6 -207 -5
-266 -4 -279 6 -23 18 -82 183 -102 288 -39 204 -49 240 -69 240 -14 0 -2
-276 17 -380 22 -115 21 -146 -5 -163 -17 -13 -24 -13 -47 -1 -15 7 -108 20
-207 29 -172 15 -278 33 -429 73 -81 21 -79 19 -90 158 -4 49 -21 184 -37 299
-16 116 -29 238 -29 271 0 68 -14 90 -50 79 -12 -4 -28 -22 -36 -41 -23 -56
-36 -236 -30 -414 10 -245 13 -264 52 -311 19 -23 34 -47 34 -55 0 -20 53
-124 76 -150 11 -12 37 -26 59 -33 22 -6 49 -18 59 -26 18 -13 18 -18 6 -55
-11 -32 -11 -58 -2 -134 14 -112 15 -215 3 -235 -23 -36 -37 14 -72 244 -22
148 -39 195 -65 174 -23 -19 -17 -164 16 -358 30 -180 56 -385 67 -525 5 -65
4 -78 -8 -74 -8 2 -15 18 -17 34 -8 83 -24 96 -48 38 -34 -82 -26 -465 12
-596 19 -65 74 -177 115 -238 37 -54 154 -134 227 -155 104 -31 156 -36 507
-45 376 -9 557 -20 738 -45 59 -8 148 -15 197 -15 80 0 105 -5 232 -48 l143
-47 265 0 c197 1 294 5 378 18 139 21 286 22 471 2 77 -8 234 -21 348 -29 229
-17 259 -24 367 -89 39 -24 87 -49 106 -56 87 -32 477 -22 595 15 141 44 368
26 845 -67 85 -17 183 -31 216 -33 54 -2 67 -7 108 -40 140 -116 479 -160 731
-96 77 20 115 23 260 23 172 -1 267 -12 455 -52 52 -11 151 -26 220 -32 181
-16 326 -42 490 -89 80 -22 177 -45 215 -50 90 -12 280 -12 372 0 77 10 99 19
175 74 26 19 78 46 115 60 37 15 102 51 143 81 129 94 182 127 287 176 122 57
166 86 280 185 47 41 97 81 112 89 15 7 66 18 114 23 130 14 173 40 325 191
107 107 135 131 178 145 153 51 277 168 343 326 13 30 34 69 47 85 13 17 32
51 43 76 25 55 78 316 86 419 4 41 13 104 22 140 11 45 13 86 8 135 -8 84 -24
155 -36 155 -23 0 -33 -46 -59 -266 -11 -89 -22 -169 -26 -178 -16 -41 -23 14
-17 124 6 134 -6 200 -37 200 -26 0 -26 25 1 74 32 60 47 153 59 361 9 167 5
297 -12 376 -3 15 119 126 128 116 7 -7 -21 -196 -52 -357 -29 -153 -31 -190
-6 -190 40 1 53 25 81 152 36 168 52 298 54 450 2 160 -5 187 -42 154 -18 -17
-19 -17 -25 2 -14 43 15 109 81 191 24 30 50 66 57 80 14 27 36 338 36 505 0
73 -3 91 -21 116 -28 38 -22 76 31 199 27 62 44 120 51 170 11 91 27 424 21
463 -5 36 -29 36 -44 1 -18 -43 -39 -63 -65 -63 -28 0 -28 0 -1 77 l23 63 -25
45 c-20 36 -25 60 -25 118 0 133 16 431 27 487 11 60 43 130 58 130 22 0 22
-68 -1 -381 -12 -181 -20 -332 -16 -336 5 -4 24 1 43 12 63 34 116 154 130
296 4 41 13 88 20 106 20 46 44 280 51 497 l6 189 29 16 c35 20 45 51 53 176
9 140 32 261 60 324 17 36 25 71 25 108 0 56 21 109 66 168 45 59 84 158 95
244 12 87 8 413 -5 443 -4 9 -23 26 -42 37 -23 14 -36 31 -40 50 -15 71 -80
96 -364 142 -185 30 -202 31 -217 16 -15 -15 -14 -17 11 -27 15 -5 33 -10 40
-10 38 0 96 -76 96 -125 0 -38 -13 -30 -34 21 -15 36 -23 44 -40 42 -18 -3
-21 -11 -24 -53 -3 -39 5 -72 39 -157 49 -125 48 -151 -7 -174 -19 -8 -37 -20
-40 -27 -2 -7 9 -46 26 -86 53 -123 73 -268 31 -217 -8 9 -11 25 -8 36 3 11
-4 36 -14 57 -10 20 -19 47 -19 58 0 48 -53 85 -86 60 -14 -10 -15 -17 -6 -45
7 -18 12 -62 12 -96 0 -49 3 -64 14 -64 27 0 39 -67 33 -179 -9 -147 -33 -228
-58 -191 -6 10 -8 71 -4 170 l6 155 -56 -84 c-132 -194 -135 -257 -25 -406 25
-34 44 -72 47 -94 3 -20 -2 -95 -11 -166 -9 -72 -16 -167 -16 -210 0 -111 -43
-327 -76 -385 -14 -24 -15 -23 -10 25 14 129 27 500 22 582 -8 112 -16 132
-69 166 -65 43 -90 37 -155 -35 -154 -169 -192 -198 -192 -147 0 12 -16 36
-36 54 -48 44 -43 67 16 80 35 7 58 22 105 70 33 33 70 79 83 103 l23 42 -57
61 c-32 33 -83 77 -114 97 -93 63 -120 85 -120 100 0 8 14 33 32 56 17 22 33
43 35 45 1 2 36 -28 78 -68 41 -39 82 -71 91 -71 9 0 30 -16 47 -35 17 -19 39
-35 49 -35 71 0 195 232 227 425 17 104 21 179 31 655 10 427 25 852 50 1365
20 427 32 1043 26 1355 -8 442 -36 683 -86 765 -11 18 -20 43 -20 57 0 87
-265 442 -412 553 -79 59 -274 155 -397 195 -216 70 -413 97 -811 110 -151 5
-356 16 -455 24 -315 27 -362 30 -1625 96 -316 17 -759 41 -985 55 -225 13
-538 29 -695 35 -476 18 -840 38 -2035 115 -290 19 -807 27 -965 15z m532 -90
c68 -6 409 -24 758 -40 1053 -50 1381 -68 1885 -105 110 -8 394 -26 630 -40
822 -48 3027 -187 3185 -200 489 -43 675 -110 950 -343 240 -204 327 -329 356
-514 8 -51 27 -136 42 -188 l27 -95 -1 -445 c0 -490 -15 -1052 -44 -1755 -11
-253 -27 -678 -35 -945 -16 -505 -19 -562 -37 -645 -23 -105 -78 -195 -120
-195 -16 0 -18 9 -17 78 1 42 -4 90 -10 105 -38 97 -39 612 -2 1053 38 447 62
2462 33 2712 -30 251 -73 386 -169 531 -109 163 -313 337 -489 416 -83 37
-124 48 -230 65 -464 70 -1188 127 -1939 150 -187 6 -428 17 -535 25 -107 8
-379 24 -605 35 -225 11 -491 27 -590 35 -232 18 -491 32 -835 45 -379 15
-966 51 -1055 65 -16 3 -178 14 -360 25 -181 11 -368 25 -415 30 -47 5 -260
19 -475 30 -214 11 -414 25 -444 30 -103 19 -89 26 111 61 181 31 250 34 430
19z m-1718 -1371 c11 -145 8 -1780 -5 -2309 -9 -413 -9 -662 4 -905 l2 -50
-35 -3 c-50 -4 -93 26 -108 74 -21 72 -24 227 -13 749 6 286 15 882 21 1325
11 809 15 905 51 1058 42 180 72 202 83 61z m2495 -2820 c133 -22 211 -49 211
-72 0 -21 -32 -27 -157 -27 -159 0 -207 12 -239 60 -14 22 -23 44 -19 49 9 16
63 13 204 -10z m-457 -31 c22 -24 35 -45 30 -50 -18 -18 -116 -21 -165 -4 -36
12 -87 16 -213 16 -108 0 -163 -3 -159 -10 3 -5 17 -10 31 -10 13 0 24 -4 24
-9 0 -13 -36 -21 -132 -28 l-87 -6 -30 31 c-34 33 -41 76 -15 86 27 10 373 7
487 -5 104 -11 110 -11 135 9 38 30 50 27 94 -20z m-1073 -63 c26 -30 41 -65
27 -65 -26 0 -96 80 -96 110 0 17 33 -4 69 -45z m2723 -19 c37 -8 77 -20 89
-26 42 -23 -3 -30 -190 -30 -175 0 -290 11 -321 30 -21 13 0 31 47 41 55 12
296 2 375 -15z m-1423 0 c50 -26 125 -125 108 -143 -16 -15 -116 28 -146 63
-35 39 -38 56 -15 78 19 19 18 19 53 2z m-1024 -30 c51 -22 114 -92 169 -190
27 -49 60 -101 73 -116 14 -17 21 -35 17 -44 -9 -23 -57 -46 -94 -46 -24 0
-34 7 -48 32 -9 17 -32 48 -49 68 -18 21 -40 57 -48 81 -9 24 -38 73 -64 110
-69 94 -75 106 -53 112 34 9 63 7 97 -7z m750 -11 c23 -8 55 -12 77 -9 25 5
45 1 68 -12 43 -26 101 -116 124 -192 27 -88 23 -91 -140 -102 -175 -12 -507
-12 -538 0 -15 5 -26 17 -26 28 0 11 -16 36 -35 57 -52 55 -128 188 -112 196
6 4 73 12 147 19 74 6 176 15 225 19 134 12 166 12 210 -4z m1222 -49 c48 -55
97 -142 89 -161 -20 -53 -105 45 -156 181 -16 41 23 30 67 -20z m2220 13 c138
-18 220 -50 250 -95 14 -22 14 -27 2 -36 -18 -12 -486 -3 -594 12 -86 13 -131
33 -141 63 -10 31 18 48 92 56 104 12 302 12 391 0z m-1897 -37 c119 -8 153
-14 188 -32 34 -18 51 -21 88 -15 79 12 93 -1 219 -194 28 -42 30 -53 30 -133
0 -106 -9 -120 -56 -87 -31 22 -46 24 -214 30 -99 4 -197 13 -219 19 -68 20
-118 85 -206 266 -44 90 -80 165 -80 167 0 2 24 0 53 -4 28 -5 117 -12 197
-17z m-2948 -49 c107 -112 217 -303 226 -392 4 -43 2 -50 -18 -61 -12 -6 -28
-9 -35 -6 -19 7 -64 99 -115 236 -24 63 -58 148 -76 188 -18 39 -30 72 -25 72
4 0 23 -17 43 -37z m562 -14 c51 -60 57 -81 50 -176 -13 -174 -39 -175 -54 -3
-5 63 -16 136 -24 163 -17 55 -8 60 28 16z m3236 5 c85 -8 189 -23 230 -33 41
-10 101 -24 132 -30 44 -9 64 -20 84 -44 36 -42 143 -262 150 -309 5 -31 3
-38 -19 -53 -71 -46 -287 -41 -424 10 -37 15 -92 27 -120 29 -65 3 -74 11
-164 142 -56 84 -179 296 -179 311 0 4 90 -2 310 -23z m505 -5 c12 -13 18 -32
17 -55 -2 -31 0 -34 24 -34 57 0 120 -63 98 -98 -21 -32 -59 -26 -93 15 -32
39 -101 163 -101 182 0 18 36 12 55 -10z m-844 -31 c37 -50 92 -165 86 -181
-3 -11 -100 130 -131 191 -25 50 6 43 45 -10z m2198 17 c106 -14 213 -25 237
-25 25 0 61 -7 81 -15 74 -31 243 -239 243 -300 0 -11 20 -70 45 -130 27 -66
41 -113 35 -119 -29 -29 -418 -31 -540 -2 -36 8 -93 15 -128 14 -78 -1 -86 6
-113 99 -11 39 -42 127 -68 195 l-49 123 25 7 c28 8 323 -7 433 -22 41 -6 99
-17 129 -25 57 -15 71 -13 71 10 0 37 -109 79 -240 94 -47 5 -98 15 -115 21
-16 6 -101 16 -189 21 -87 5 -167 14 -177 20 -19 10 -25 39 -12 52 13 14 142
6 332 -18z m891 -55 c235 -28 372 -30 398 -7 19 18 21 17 56 -14 48 -41 63
-69 45 -80 -19 -12 -184 -11 -299 1 -52 5 -156 14 -232 20 -145 10 -213 29
-223 61 -15 46 8 48 255 19z m-1675 -30 c33 -6 79 -18 103 -27 35 -13 50 -14
81 -4 21 6 48 11 59 11 35 0 84 -72 151 -222 66 -150 81 -201 61 -213 -17 -11
-328 -7 -390 5 -30 6 -95 15 -144 20 -70 8 -96 16 -114 33 -26 24 -85 135 -96
181 -4 17 -26 61 -48 100 -60 102 -68 118 -61 129 8 13 318 3 398 -13z m412
-4 c58 -49 101 -184 74 -235 -7 -12 -16 -19 -21 -16 -13 8 -77 146 -94 205
-22 75 -10 89 41 46z m3201 -34 c57 -32 -6 -72 -110 -72 -70 0 -111 16 -116
44 -5 26 9 36 64 44 57 9 129 1 162 -16z m-2219 -47 c52 -45 49 -95 -6 -95
-19 0 -28 9 -43 46 -11 25 -20 52 -20 60 0 22 36 16 69 -11z m301 -29 c355
-39 460 -41 531 -12 11 5 25 -2 41 -19 50 -54 176 -340 164 -372 -10 -26 -359
-14 -641 23 -119 16 -155 26 -155 45 0 5 -26 67 -59 138 -76 167 -88 201 -70
212 8 4 23 6 34 4 11 -3 81 -11 155 -19z m2820 -11 c9 -8 43 -23 76 -33 51
-15 59 -21 62 -45 6 -47 -99 -217 -134 -217 -8 0 -14 7 -14 15 0 9 -16 57 -36
108 -19 51 -38 113 -41 140 l-6 47 38 0 c21 0 46 -7 55 -15z m-8205 -44 c0
-18 -52 -34 -61 -19 -9 14 25 40 46 36 8 -2 15 -10 15 -17z m275 9 c11 -7 5
-10 -23 -10 -23 0 -36 4 -32 10 8 13 35 13 55 0z m6936 -20 c19 -16 47 -32 62
-36 51 -11 52 -12 52 -66 0 -74 -22 -268 -30 -268 -8 0 -83 168 -110 245 -30
89 -36 155 -13 155 3 0 21 -13 39 -30z m-109 -48 c32 -62 43 -125 24 -137 -23
-14 -46 35 -88 188 -4 14 0 18 13 15 11 -2 32 -30 51 -66z m-944 9 c47 -45
115 -177 99 -193 -22 -22 -107 62 -166 163 -14 25 -26 53 -26 63 0 26 49 9 93
-33z m-6195 -91 c9 -11 31 -57 47 -102 l30 -83 40 -5 40 -5 7 -64 c8 -68 20
-93 38 -75 6 6 14 81 19 167 5 105 12 161 21 172 12 15 14 15 23 -1 6 -10 12
-86 14 -168 4 -169 9 -216 24 -216 18 0 30 54 44 202 15 150 25 188 49 188 21
0 26 -34 26 -205 0 -163 6 -235 20 -235 19 0 32 75 40 223 7 116 13 165 25
183 19 29 21 29 63 4 52 -32 64 -72 51 -172 -7 -49 -8 -139 -5 -219 l7 -136
-98 -2 c-54 -1 -107 -4 -118 -7 -11 -3 -58 4 -105 16 -47 11 -137 23 -200 27
-100 5 -117 4 -129 -11 -19 -23 -58 -20 -72 5 -15 29 -4 93 24 131 21 29 22
37 19 209 -1 99 0 184 3 189 10 16 34 11 53 -10z m8536 -62 c9 -80 7 -252 -4
-353 -11 -96 -44 -180 -70 -180 -18 0 -18 18 1 103 16 78 4 175 -41 316 -17
54 -29 100 -26 103 12 12 38 -9 51 -41 8 -19 19 -37 24 -41 14 -8 31 28 31 67
0 40 10 78 20 78 4 0 10 -24 14 -52z m-8717 26 c8 -21 -13 -42 -28 -27 -13 13
-5 43 11 43 6 0 13 -7 17 -16z m1303 -19 c58 -8 206 -19 330 -25 124 -6 262
-13 308 -16 l82 -6 0 -51 c0 -29 -7 -115 -15 -192 -20 -192 -19 -261 5 -312
10 -24 26 -43 35 -43 8 0 15 3 15 8 1 4 3 75 5 157 3 135 33 366 49 382 18 18
21 -36 18 -310 -2 -181 1 -312 6 -327 l10 -25 15 35 c23 53 45 222 58 430 12
210 15 230 31 230 8 0 9 -81 3 -302 -10 -390 -11 -382 24 -390 11 -2 14 7 12
38 -1 22 7 99 19 170 12 71 25 199 29 284 4 86 10 161 15 168 7 12 78 17 88 6
4 -3 0 -20 -7 -38 -17 -41 -52 -276 -61 -411 -7 -106 9 -175 39 -175 23 0 27
22 37 224 13 282 42 401 96 401 27 0 33 -62 37 -335 l2 -225 -32 -56 c-18 -31
-33 -65 -33 -76 0 -37 -23 -45 -112 -40 -46 2 -143 14 -216 25 -169 27 -376
46 -420 38 -19 -3 -45 -15 -57 -26 -22 -21 -27 -21 -116 -11 -52 6 -205 20
-341 31 -136 11 -268 25 -292 31 -76 19 -100 69 -39 83 15 4 127 9 248 12 242
7 447 -6 497 -32 27 -14 32 -14 54 1 31 20 27 34 -17 60 -32 19 -55 20 -343
23 -203 2 -332 -1 -375 -8 -118 -21 -129 -22 -135 -12 -4 6 7 25 24 42 16 17
30 36 30 43 0 7 -11 55 -25 107 -27 104 -32 201 -14 295 16 86 37 133 62 139
35 7 252 -4 367 -19z m-1563 -35 c15 -45 17 -400 2 -453 -12 -45 -37 -47 -57
-4 -33 69 -37 425 -5 475 20 32 46 24 60 -18z m-1633 -9 c8 -13 -37 -76 -54
-76 -19 0 -26 51 -9 71 13 15 55 19 63 5z m-464 -41 c0 -31 -21 -124 -31 -134
-13 -14 -11 84 2 123 12 35 29 42 29 11z m1760 -9 c0 -10 -58 -49 -118 -80
-36 -18 -42 -19 -42 -6 0 21 43 63 83 80 36 16 77 19 77 6z m454 -37 c7 -19
-13 -94 -26 -94 -9 0 -11 94 -1 103 11 11 20 8 27 -9z m-2854 -29 c10 -9 22
-33 27 -53 6 -24 18 -41 38 -52 34 -19 59 -77 43 -97 -22 -27 -150 -9 -162 22
-3 8 -41 35 -85 61 -44 25 -86 52 -92 60 -14 17 -8 44 13 58 14 8 22 6 40 -11
l21 -22 35 31 c29 25 41 29 70 24 19 -3 43 -12 52 -21z m6210 -19 c41 -9 151
-24 243 -35 93 -10 173 -23 178 -28 7 -7 6 -79 -2 -219 -13 -235 -7 -375 17
-412 10 -16 19 -21 26 -14 9 9 20 133 37 434 5 91 14 176 20 187 7 13 22 21
36 21 23 0 25 -4 25 -47 0 -27 -7 -106 -15 -178 -20 -180 -19 -260 5 -314 16
-35 23 -41 34 -32 10 9 15 62 19 234 3 140 10 241 18 272 14 49 15 50 57 53
32 2 44 -1 49 -14 3 -9 -3 -82 -13 -163 -23 -178 -26 -400 -5 -425 13 -15 16
-15 30 -2 12 12 17 52 23 174 9 187 39 402 62 437 54 82 79 -37 56 -265 -35
-345 -34 -391 2 -398 26 -5 37 49 53 278 9 120 19 255 23 301 l7 84 65 6 c36
4 65 6 66 5 1 0 -2 -68 -7 -151 -4 -82 -8 -241 -8 -353 -1 -172 1 -203 14
-208 9 -4 24 4 36 21 16 20 27 25 45 21 29 -8 57 20 72 72 16 54 30 174 43
367 6 94 15 180 20 193 8 21 13 22 123 22 64 0 240 -7 393 -15 153 -8 318 -16
366 -17 60 -2 90 -7 96 -16 5 -8 11 -147 15 -310 6 -300 12 -376 26 -367 12 7
20 110 30 385 5 135 11 252 15 262 8 19 46 31 59 18 10 -10 21 -167 32 -472 3
-98 10 -178 15 -178 25 0 32 68 36 339 2 184 8 290 15 298 17 21 48 15 58 -12
12 -33 25 -512 16 -587 -7 -52 -10 -58 -44 -76 -20 -10 -53 -28 -72 -38 -31
-17 -57 -19 -240 -19 -113 0 -294 3 -402 5 l-197 5 -39 38 c-21 20 -46 37 -55
37 -10 0 -45 -11 -79 -25 -66 -27 -139 -32 -218 -14 -25 5 -174 13 -331 17
l-286 8 -36 27 c-43 32 -46 33 -125 6 -43 -15 -66 -18 -75 -11 -18 15 -116 30
-272 42 -207 16 -281 27 -349 56 -115 48 -115 48 -159 33 -47 -16 -234 -19
-257 -4 -11 7 -4 17 31 47 51 44 50 33 30 306 -6 85 -11 191 -11 237 0 71 3
84 18 89 9 2 118 3 242 2 161 -3 246 -8 300 -20z m5144 -42 c8 -61 7 -268 -2
-318 -14 -76 -29 -51 -35 56 -4 67 -14 130 -30 180 -13 42 -22 80 -18 86 6 9
61 41 72 42 4 0 9 -21 13 -46z m-9204 -1 c-1 -21 -61 -63 -90 -63 -19 0 -10
29 16 49 26 20 74 29 74 14z m420 -99 c0 -95 -12 -125 -24 -56 -8 48 -8 126 0
146 14 36 24 -1 24 -90z m-185 70 c-36 -30 -166 -96 -174 -89 -15 16 143 115
184 114 17 0 16 -3 -10 -25z m5066 -198 c0 -155 -10 -251 -37 -341 -37 -128
-49 -83 -35 131 7 88 16 201 22 253 23 205 50 182 50 -43z m1767 152 c7 -7 12
-18 12 -25 0 -8 15 -29 33 -48 19 -19 42 -53 52 -75 18 -40 18 -40 -9 -108
-41 -106 -67 -262 -50 -306 9 -24 48 -19 77 10 32 32 42 79 28 130 -46 164 65
397 159 334 13 -9 12 -13 -6 -28 -12 -9 -36 -25 -53 -34 -17 -9 -37 -29 -43
-44 -11 -24 -10 -28 9 -41 21 -14 21 -16 5 -51 -13 -29 -17 -76 -18 -222 l-2
-185 -34 -17 c-26 -14 -58 -18 -145 -18 -63 0 -114 -4 -118 -10 -3 -5 19 -21
49 -34 60 -27 62 -30 45 -88 -11 -42 -23 -47 -50 -22 -10 9 -29 17 -41 18 -18
1 -24 9 -28 36 -4 19 -12 45 -18 56 -20 38 -72 74 -105 74 -71 0 -77 23 -77
293 1 326 15 410 64 391 16 -6 18 -16 13 -83 -27 -373 -31 -569 -11 -548 10 9
33 181 50 367 9 102 19 203 23 225 l6 40 65 11 c88 16 104 16 118 2z m923 -14
c12 -11 59 -19 159 -29 148 -14 175 -24 206 -70 24 -37 11 -68 -47 -110 -28
-20 -58 -50 -67 -66 -11 -20 -40 -42 -84 -65 -36 -18 -69 -38 -73 -44 -16 -25
-98 -61 -185 -79 -77 -17 -128 -37 -275 -111 -170 -85 -263 -118 -299 -104
-11 4 -11 11 1 45 10 30 12 68 7 147 -6 104 4 170 25 158 4 -3 11 -25 15 -48
4 -23 15 -56 25 -73 10 -16 26 -54 36 -82 26 -83 43 -70 55 41 5 39 22 114 39
167 35 109 38 135 15 167 -23 33 -11 38 41 17 26 -9 49 -14 53 -11 11 12 6 74
-7 90 -7 8 -36 23 -64 33 l-52 18 202 5 c111 3 205 9 209 13 13 12 47 8 65 -9z
m-8241 -34 c0 -13 -22 -23 -117 -55 -45 -14 -92 -25 -105 -23 -20 3 -17 7 22
27 84 45 200 75 200 51z m504 -57 c-11 -31 -57 -54 -75 -36 -15 15 4 49 39 67
35 19 49 6 36 -31z m-394 -12 c0 -9 -99 -80 -147 -105 -23 -12 -34 -13 -39 -4
-9 15 35 50 110 88 62 32 76 35 76 21z m-496 -40 c10 -15 -21 -33 -50 -29 -27
3 -27 4 -10 21 19 18 51 23 60 8z m1411 -27 c0 -19 -5 -29 -15 -29 -16 0 -25
28 -16 51 10 25 31 10 31 -22z m-3116 9 c15 -29 40 -195 56 -376 23 -259 18
-268 -33 -57 -39 160 -52 248 -52 353 0 94 8 118 29 80z m394 -19 c222 -20
265 -32 312 -87 39 -45 310 -592 322 -648 5 -29 4 -32 -13 -25 -41 16 -128 26
-358 41 -276 18 -365 29 -407 50 -35 19 -74 75 -88 130 -26 100 -73 499 -64
536 7 24 59 25 296 3z m705 9 c-10 -2 -26 -2 -35 0 -10 3 -2 5 17 5 19 0 27
-2 18 -5z m1321 -4 c37 -13 39 -23 9 -43 -46 -29 -258 -52 -258 -28 0 14 44
50 80 66 41 18 124 20 169 5z m9250 -46 c1 -12 -4 -34 -10 -50 -9 -25 -11 -26
-20 -10 -10 17 -4 90 9 111 7 12 20 -21 21 -51z m-10010 -27 c46 -9 95 -23
109 -32 32 -21 155 -277 176 -365 18 -79 71 -198 122 -275 35 -52 36 -56 20
-72 -14 -15 -45 -17 -214 -20 -233 -4 -377 9 -407 38 -12 11 -34 20 -50 20
-21 0 -41 13 -76 49 -37 39 -71 98 -157 273 -98 200 -147 309 -166 375 -6 20
-3 22 36 26 97 11 524 -2 607 -17z m2225 -12 c25 -9 0 -34 -34 -34 -37 0 -45
12 -18 27 27 14 31 15 52 7z m-1274 -69 c146 -28 138 -22 229 -205 44 -88 99
-209 122 -268 55 -142 70 -169 123 -232 25 -30 44 -55 42 -57 -7 -8 -552 18
-596 28 -83 18 -143 77 -203 194 -36 73 -204 465 -212 496 -8 32 12 50 68 60
66 12 334 2 427 -16z m8797 -32 c-3 -10 -5 -4 -5 12 0 17 2 24 5 18 2 -7 2
-21 0 -30z m-10478 -60 c19 -13 47 -23 62 -23 32 0 42 -15 82 -121 14 -36 39
-93 56 -127 24 -47 29 -67 21 -77 -16 -19 -80 -2 -107 30 -50 57 -173 299
-173 339 0 18 1 18 59 -21z m2536 27 c212 -10 316 -23 350 -46 47 -31 154
-186 188 -273 10 -23 35 -69 57 -100 21 -32 52 -86 69 -122 17 -35 56 -95 86
-132 30 -38 55 -72 55 -77 0 -17 -55 -20 -375 -20 -343 0 -431 7 -496 41 -34
17 -46 34 -99 142 -33 67 -69 158 -80 203 -11 44 -40 123 -64 175 -50 107 -79
189 -71 198 4 3 43 9 88 12 45 3 87 6 92 7 6 1 96 -3 200 -8z m-1458 -62 c67
-62 127 -147 161 -230 45 -111 46 -114 21 -137 -33 -31 -64 -26 -93 12 -28 36
-153 320 -163 368 -8 43 18 38 74 -13z m8612 -59 c14 -62 14 -149 1 -149 -21
0 -70 206 -54 231 12 20 41 -24 53 -82z m-10773 -6 c3 -18 3 -41 -1 -50 -6
-17 -7 -17 -16 -1 -6 10 -12 42 -15 70 -5 49 -4 52 10 34 9 -11 19 -34 22 -53z
m11693 17 c17 0 31 -5 31 -10 0 -19 -131 -140 -151 -140 -25 0 -24 18 6 86 34
76 43 86 65 74 10 -6 32 -10 49 -10z m-7074 -35 c77 -8 202 -23 278 -35 76
-11 157 -20 179 -20 36 0 46 -6 85 -47 66 -71 177 -221 206 -278 14 -27 37
-61 52 -75 60 -55 159 -225 153 -263 -5 -32 -50 -37 -253 -27 -219 12 -265 18
-289 39 -14 13 -49 17 -174 21 l-157 5 -14 30 c-8 17 -36 58 -62 93 -48 64
-62 95 -74 165 -4 23 -17 47 -35 63 -15 14 -31 40 -34 58 -5 25 -83 172 -153
289 -6 9 56 5 292 -18z m-1407 3 c22 -29 104 -234 112 -282 4 -28 18 -77 30
-109 12 -32 19 -61 16 -64 -9 -9 -83 27 -104 51 -33 37 -61 94 -97 199 -40
120 -42 140 -15 185 21 34 41 41 58 20z m2548 -63 c247 -27 325 -40 364 -60
44 -23 77 -67 145 -193 36 -68 104 -186 150 -264 76 -125 116 -211 104 -222
-7 -6 -640 14 -687 22 l-43 7 -40 105 c-82 219 -227 489 -306 572 -19 20 -32
41 -29 47 11 17 90 13 342 -14z m4640 -152 c-11 -216 -27 -396 -38 -416 -11
-21 -55 -40 -75 -33 -10 4 -6 20 19 68 l32 63 6 215 c3 118 9 221 13 228 5 6
18 12 29 12 20 0 21 -3 14 -137z m-6022 13 c61 -125 96 -210 96 -233 l0 -24
-27 21 c-18 14 -35 19 -53 15 -34 -7 -50 12 -79 91 -12 33 -40 82 -62 109 -46
55 -48 70 -14 79 15 4 30 18 36 33 l10 26 34 -33 c19 -18 45 -56 59 -84z
m2464 33 c88 -11 94 -13 123 -48 31 -37 114 -198 184 -356 22 -49 65 -129 97
-177 75 -115 92 -148 80 -160 -11 -11 -209 13 -351 42 -156 32 -194 66 -296
270 -65 130 -112 254 -124 330 -6 34 -17 73 -26 86 -8 13 -15 26 -15 30 0 9
210 -2 328 -17z m3227 -115 c-3 -29 -10 -56 -15 -59 -20 -12 -22 101 -3 155
11 31 25 -42 18 -96z m-4646 81 c46 -23 171 -229 171 -280 0 -35 -25 -32 -63
8 -53 56 -187 241 -187 259 0 29 37 35 79 13z m2177 -25 c174 -14 338 -38 393
-59 35 -12 48 -24 56 -46 5 -17 30 -62 56 -100 57 -86 117 -204 159 -313 17
-45 47 -105 66 -134 19 -28 33 -53 30 -55 -2 -3 -127 -5 -278 -6 l-273 -2 -59
30 c-81 41 -196 158 -235 238 -82 171 -181 408 -181 432 0 25 82 29 266 15z
m2214 -4 c0 -23 -21 -76 -31 -76 -13 0 -41 60 -35 76 7 18 66 19 66 0z m850
-78 c0 -86 -14 -158 -30 -158 -6 0 -10 26 -10 57 0 70 19 183 31 183 5 0 9
-37 9 -82z m-4168 7 c33 -62 106 -242 114 -284 6 -29 5 -31 -23 -31 -21 0 -41
11 -67 38 -69 68 -204 282 -191 302 12 19 48 29 95 27 43 -2 46 -4 72 -52z
m2398 -4 l225 -6 23 -85 c12 -47 34 -148 48 -225 14 -77 32 -166 40 -198 12
-45 12 -62 3 -73 -28 -34 -182 -63 -470 -88 l-66 -6 -135 227 c-153 257 -258
446 -258 464 0 9 42 10 183 4 100 -4 283 -11 407 -14z m476 -56 c14 -34 44
-318 44 -417 0 -53 -3 -55 -82 -42 -44 8 -48 16 -78 183 -21 118 -26 211 -12
211 5 0 17 -7 28 -15 10 -8 25 -15 32 -15 17 0 42 60 42 100 0 39 8 38 26 -5z
m2141 -45 c-9 -116 -26 -137 -27 -33 0 86 5 113 23 113 6 0 8 -27 4 -80z
m-4132 -125 c102 -212 106 -235 38 -235 -75 0 -133 58 -205 206 -21 44 -40 93
-41 109 -2 25 2 30 32 36 18 4 45 16 60 27 24 19 27 19 40 4 7 -10 42 -75 76
-147z m2795 106 c0 -19 -23 -71 -31 -71 -12 0 -11 37 1 61 11 19 30 26 30 10z
m-1872 -23 c27 -30 154 -263 172 -317 7 -22 10 -44 6 -48 -14 -15 -87 -6 -108
13 -27 25 -125 228 -139 292 -7 28 -9 57 -6 66 9 23 50 20 75 -6z m-5993 -106
c69 -6 80 -9 80 -26 0 -30 -28 -41 -72 -28 -21 6 -96 14 -168 17 -136 6 -169
16 -113 33 55 18 80 21 138 15 30 -2 91 -8 135 -11z m8905 -38 c18 -47 11
-226 -15 -381 -14 -81 -30 -162 -35 -179 -21 -62 -171 -174 -234 -174 -16 0
-41 11 -58 25 -16 14 -40 25 -53 25 -13 0 -27 6 -31 12 -7 12 -27 357 -28 503
l-1 60 45 1 c60 3 122 26 197 75 37 24 81 44 110 49 83 14 93 12 103 -16z
m289 -119 c0 -55 -4 -109 -8 -120 -8 -19 -8 -19 -21 4 -12 24 -8 158 7 219 12
48 23 -3 22 -103z m-910 59 c-26 -27 -39 -31 -39 -10 0 18 40 47 52 40 5 -4
-1 -17 -13 -30z m-1122 -11 c-3 -10 -5 -4 -5 12 0 17 2 24 5 18 2 -7 2 -21 0
-30z m-6507 -10 c4 -4 4 -89 -1 -188 -4 -99 -8 -286 -8 -416 -1 -259 -4 -273
-56 -267 -38 4 -61 43 -82 139 -13 59 -16 131 -16 349 0 323 5 388 35 399 23
8 117 -4 128 -16z m-238 -15 c10 -18 14 -123 16 -443 2 -317 -1 -415 -9 -400
-19 32 -41 45 -79 45 -50 0 -54 8 -20 40 l29 27 -15 59 c-22 90 -28 281 -15
444 14 171 24 220 47 237 24 18 31 16 46 -9z m433 -331 c-1 -250 -5 -367 -14
-404 -22 -92 -74 -132 -100 -77 -33 73 -42 804 -10 825 8 5 39 9 69 9 l55 0 0
-353z m376 311 c39 -21 40 -22 88 -4 45 16 53 16 95 2 25 -8 134 -25 243 -36
109 -11 236 -27 283 -35 47 -8 201 -22 342 -30 141 -8 268 -19 282 -24 32 -13
45 -57 53 -176 6 -92 -10 -384 -28 -509 -8 -57 -7 -72 5 -84 12 -12 16 -11 29
9 37 60 57 238 57 531 0 244 -5 230 79 241 l44 5 -7 -111 c-11 -192 -10 -647
2 -659 42 -42 72 363 49 645 l-8 97 46 0 c66 0 112 -22 118 -57 22 -115 13
-301 -23 -463 -10 -49 -22 -117 -26 -152 -9 -82 -34 -131 -76 -149 -67 -28
-369 -8 -398 26 -9 10 -9 71 -1 234 12 250 13 507 2 536 -11 28 -19 -27 -36
-277 -22 -313 -42 -418 -78 -418 -30 0 -34 45 -45 462 -4 147 -11 270 -15 274
-4 4 -15 1 -24 -8 -13 -14 -17 -59 -24 -280 -5 -144 -11 -273 -14 -285 -3 -13
-21 -31 -43 -41 -20 -11 -58 -31 -83 -46 -132 -76 -604 -57 -851 35 -43 16
-111 33 -152 38 -82 10 -123 33 -158 89 -25 41 -36 174 -37 449 l-1 182 48 4
c26 2 87 4 136 5 74 2 94 -1 127 -20z m6947 -65 c14 -32 16 -69 15 -221 -2
-193 -18 -394 -36 -427 -5 -11 -23 -35 -38 -53 -38 -43 -38 -69 1 -77 32 -7
40 -29 14 -39 -29 -11 -89 -6 -112 9 -20 15 -20 18 -8 38 23 35 66 320 82 542
5 70 2 100 -10 135 -30 85 -14 130 45 130 25 0 33 -6 47 -37z m-997 -20 c11
-30 5 -58 -12 -48 -14 9 -31 54 -23 66 10 17 24 9 35 -18z m735 -21 c15 -10
34 -47 34 -67 0 -39 -169 -82 -375 -95 -143 -10 -210 -28 -246 -66 l-24 -26
22 -17 c12 -10 52 -25 90 -35 68 -16 69 -16 153 9 46 14 95 25 108 25 14 0 81
18 148 40 133 43 149 46 160 28 29 -47 -261 -138 -486 -153 -164 -11 -202 -37
-112 -78 38 -17 69 -22 167 -24 66 -1 127 -3 135 -3 34 -3 -53 -29 -177 -54
-190 -38 -220 -52 -160 -77 21 -9 73 -14 152 -14 117 0 123 1 235 43 136 51
216 64 226 37 10 -25 -21 -48 -119 -86 -99 -38 -236 -74 -366 -95 -131 -21
-161 -31 -161 -54 0 -31 68 -43 203 -37 100 5 133 11 225 43 132 46 170 55
178 42 11 -17 -80 -93 -156 -130 -127 -62 -172 -73 -300 -73 -140 0 -162 12
-211 110 l-31 63 -8 280 c-4 154 -11 286 -15 293 -20 31 -28 -54 -34 -347 l-7
-312 -37 -75 c-50 -102 -95 -141 -179 -156 -119 -21 -108 -40 -108 182 0 209
21 476 45 570 8 32 15 61 15 63 0 2 7 4 17 4 17 0 17 -42 -3 -592 -5 -119 -4
-178 3 -178 14 0 21 48 33 225 33 477 29 461 113 491 53 20 55 22 98 117 19
43 29 52 98 86 140 69 137 69 177 30 l34 -33 58 13 c31 7 89 19 127 26 39 8
97 25 130 39 60 25 110 32 131 18z m1504 -35 c0 -77 -33 -354 -45 -384 -22
-53 -39 -42 -30 20 4 28 10 120 14 204 6 127 10 156 26 173 26 28 35 25 35
-13z m-1035 -199 c7 -123 -6 -261 -25 -273 -14 -9 -40 25 -40 53 0 15 6 65 14
112 8 47 18 139 22 205 9 141 17 114 29 -97z m-5026 161 c167 -35 201 -39 407
-49 143 -6 212 -13 239 -24 24 -10 68 -16 121 -16 l84 0 -1 -122 c0 -68 -4
-201 -8 -295 -10 -200 -16 -217 -83 -228 -23 -4 -62 -16 -86 -26 -108 -47
-345 -93 -477 -93 -100 0 -165 17 -313 84 -81 36 -125 50 -196 65 -28 6 -29 7
-23 58 17 137 36 375 42 497 9 201 -1 184 107 176 49 -4 133 -16 187 -27z
m991 -89 c13 -8 15 -62 15 -384 0 -287 3 -378 13 -387 35 -36 42 44 54 631 l3
125 30 -3 c61 -7 60 5 63 -432 l3 -399 -88 6 c-127 8 -153 19 -181 73 -25 48
-25 61 -3 225 5 39 12 169 16 290 4 120 11 230 16 242 9 24 33 29 59 13z
m2500 -37 c0 -5 -9 -62 -19 -128 -31 -190 -40 -296 -47 -542 l-7 -230 -98 -7
c-119 -7 -146 1 -154 47 -15 80 34 671 61 736 17 42 52 81 71 81 17 0 16 -15
-1 -65 -23 -64 -38 -263 -40 -497 -1 -152 2 -220 11 -228 17 -18 21 6 28 190
7 193 19 324 45 470 17 102 19 106 60 142 38 34 90 51 90 31z m-2120 -147 c0
-192 -18 -542 -31 -589 -6 -21 -19 -47 -31 -59 -20 -20 -21 -20 -39 -2 -15 16
-20 47 -30 179 -13 183 -5 581 13 602 6 8 32 13 64 13 l54 0 0 -144z m191 108
c29 -5 28 35 9 -429 -11 -268 -19 -318 -51 -335 -32 -18 -78 -2 -100 33 -18
28 -20 43 -14 122 3 50 12 202 20 338 8 136 20 257 26 268 11 21 17 22 110 3z
m994 -25 c220 -24 290 -37 317 -59 21 -16 21 -19 9 -153 -8 -87 -10 -195 -6
-299 5 -105 3 -172 -3 -189 -14 -36 -105 -66 -237 -79 -131 -13 -498 -13 -605
0 -161 19 -351 97 -379 154 -31 65 -23 598 9 630 13 13 375 24 580 18 74 -2
216 -12 315 -23z m634 12 c16 -10 14 -17 -23 -119 -20 -52 -32 -170 -46 -427
-14 -276 -15 -281 -53 -294 -16 -6 -58 -11 -92 -11 l-62 0 4 103 c22 594 38
712 103 744 29 15 148 17 169 4z m1433 -133 c-5 -5 -24 -8 -42 -6 l-33 3 39
28 c38 27 39 27 42 6 2 -12 -1 -26 -6 -31z m1138 -40 c0 -13 -7 -97 -15 -188
-19 -211 -19 -271 0 -296 22 -29 18 -47 -22 -102 -49 -66 -95 -92 -163 -92
l-52 0 4 283 c3 243 5 286 20 308 11 17 49 40 115 67 54 23 101 41 106 42 4 0
7 -10 7 -22z m-315 -293 c7 -84 -7 -244 -26 -292 l-11 -28 -10 25 c-14 37 -6
349 11 379 19 34 28 13 36 -84z m-10712 69 c133 -21 433 -20 656 1 216 21 243
15 274 -53 19 -44 21 -69 27 -354 l5 -306 -55 -7 c-30 -3 -107 -16 -170 -27
-137 -24 -287 -21 -500 10 -74 11 -175 24 -223 27 -106 9 -107 11 -107 128 0
126 -33 419 -61 545 -11 52 -5 66 28 57 13 -3 70 -13 126 -21z m10577 -54 c0
-32 -14 -40 -24 -14 -10 27 -7 44 9 44 10 0 15 -10 15 -30z m-234 -17 c10 -37
-3 -272 -20 -373 -31 -184 -56 -268 -97 -324 -16 -22 -29 -52 -29 -67 0 -15
-8 -36 -17 -48 -33 -39 -222 -89 -270 -70 -39 15 -46 72 -40 309 9 319 24 378
101 400 59 16 219 105 286 158 61 49 77 52 86 15z m-9205 -9 c10 -12 15 -89
21 -302 4 -158 12 -332 17 -387 6 -55 6 -104 2 -108 -5 -5 -19 -7 -32 -5 -19
2 -24 10 -25 40 -2 20 -8 54 -13 76 -24 86 -32 155 -43 342 -17 284 -16 311
18 344 20 20 38 20 55 0z m216 -69 c7 -28 17 -188 22 -379 6 -182 13 -341 17
-353 9 -32 40 -29 49 5 4 15 4 185 0 377 -8 386 -5 409 42 403 46 -5 54 -39
64 -262 14 -318 27 -530 35 -549 9 -24 21 -21 28 6 3 12 6 196 6 409 0 213 3
389 8 391 4 3 40 7 81 8 l73 4 -2 -275 c-2 -354 -13 -587 -26 -600 -11 -11
-239 -34 -344 -35 -86 0 -146 26 -159 69 -5 17 -12 99 -15 181 -3 83 -10 224
-16 315 -19 287 1 359 94 341 27 -6 33 -13 43 -56z m604 28 c69 -25 86 -27
239 -27 91 -1 249 5 352 13 181 14 190 14 325 -8 138 -23 228 -54 281 -97 l24
-20 -9 -279 -8 -280 -35 -17 c-108 -52 -311 -78 -597 -78 -178 0 -191 3 -279
60 -67 43 -274 77 -351 57 -55 -14 -58 5 -58 358 0 171 3 318 7 328 10 23 21
22 109 -10z m1930 -365 c-6 -128 -13 -278 -14 -333 -3 -108 -18 -152 -60 -180
-30 -20 -249 -30 -411 -20 -108 6 -121 9 -144 32 -19 19 -23 30 -17 51 10 33
9 90 0 262 -7 128 5 341 21 383 10 27 46 17 62 -16 27 -55 33 -160 26 -399 -6
-222 -5 -238 11 -244 37 -15 42 15 49 323 4 199 11 305 19 322 9 19 23 27 51
32 l39 6 -7 -71 c-10 -122 -19 -593 -12 -612 20 -51 36 68 57 401 6 104 17
208 23 230 11 37 15 40 46 40 64 0 69 -40 49 -390 -12 -216 -9 -251 27 -267
13 -6 27 -8 32 -4 4 4 12 153 17 331 11 359 8 344 81 370 68 24 69 22 55 -247z
m389 228 c129 -9 300 -18 380 -22 161 -7 247 -25 367 -77 l78 -33 5 -285 c3
-156 4 -290 2 -296 -2 -7 -11 -13 -19 -13 -8 0 -47 -14 -86 -32 -107 -46 -161
-58 -278 -58 -114 0 -425 40 -499 64 -25 8 -83 30 -130 49 -47 19 -93 37 -103
41 -41 14 -47 59 -47 374 0 164 2 301 4 303 5 5 -2 5 326 -15z m1571 -143 c11
-13 14 -58 12 -212 -2 -220 -19 -505 -33 -566 -20 -87 -64 -92 -386 -44 -114
16 -218 28 -231 24 -40 -10 -28 -40 25 -63 108 -45 230 -61 541 -71 231 -7
255 -9 267 -26 11 -14 14 -60 14 -185 0 -268 -25 -400 -83 -444 -24 -18 -46
-22 -134 -24 -58 -2 -141 1 -183 7 -43 7 -165 18 -271 26 -202 15 -273 30
-305 67 -17 18 -19 43 -22 237 -2 119 0 254 3 300 7 80 9 86 48 128 52 56 55
79 57 353 2 268 12 467 25 479 6 5 31 7 56 4 l46 -5 7 -279 c4 -154 11 -291
17 -306 9 -25 11 -25 27 -9 15 15 18 51 22 307 5 311 5 308 58 309 15 0 26 -8
32 -26 6 -14 10 -161 10 -343 0 -245 3 -321 13 -335 31 -43 38 3 48 316 5 167
11 317 14 335 6 37 19 43 87 43 l51 0 -5 -252 c-6 -255 -1 -398 12 -398 12 0
20 68 35 304 8 122 19 244 25 272 18 78 68 117 101 77z m484 -22 c66 -6 179
-10 251 -10 143 -1 222 -19 340 -79 l56 -28 -1 -210 c-1 -115 -4 -262 -7 -326
l-7 -118 -51 -18 c-154 -57 -141 -56 -344 -37 -350 31 -432 49 -574 122 l-88
45 0 212 c0 117 3 267 7 334 6 120 6 121 32 126 32 6 227 -1 386 -13z m4243
-129 c3 -95 1 -121 -12 -137 l-16 -20 -16 20 c-13 17 -15 37 -10 121 6 116 20
164 39 145 8 -8 13 -55 15 -129z m-10771 76 c-2 -13 -4 -5 -4 17 -1 22 1 32 4
23 2 -10 2 -28 0 -40z m9121 25 c5 -17 -26 -29 -40 -15 -6 6 -7 15 -3 22 9 14
37 9 43 -7z m348 -58 c-3 -22 -15 -159 -26 -304 -22 -296 -21 -289 -109 -393
-72 -84 -101 -104 -193 -133 -160 -51 -338 -60 -449 -24 -60 19 -71 20 -169 8
-189 -22 -289 2 -354 84 -26 32 -30 45 -28 95 1 31 2 171 3 310 l3 252 54 0
c41 0 74 9 136 37 l81 37 260 7 c314 9 403 15 518 36 48 8 104 12 125 9 85
-15 96 -13 112 21 14 29 16 31 28 15 9 -11 12 -31 8 -57z m435 -432 c-7 -87
-14 -120 -31 -148 -22 -36 -49 -51 -31 -16 19 34 30 144 41 381 6 129 14 246
18 260 11 39 14 -351 3 -477z m-271 447 c7 -14 6 -75 -5 -212 -17 -222 -19
-395 -5 -432 12 -32 45 -34 61 -5 6 12 17 114 25 228 7 113 16 222 18 241 18
126 29 180 38 180 14 0 3 -302 -16 -494 -15 -149 -13 -166 20 -166 58 0 10
-39 -79 -65 -73 -21 -106 -10 -148 48 -35 49 -36 59 -9 341 24 242 36 314 58
339 20 22 29 22 42 -3z m-1545 -154 c12 -208 1 -448 -24 -541 -17 -62 -21 -95
-17 -147 l6 -68 -187 -2 c-352 -5 -353 -5 -373 20 -11 14 -21 48 -25 94 -9 88
-18 108 -48 105 -21 -2 -22 1 -20 68 12 405 16 502 25 513 6 6 23 12 38 12 48
0 52 -28 53 -351 2 -207 5 -293 13 -296 6 -2 14 4 18 13 4 10 15 143 26 297
20 303 25 321 79 315 38 -5 43 -41 35 -273 -11 -302 -11 -378 2 -382 28 -9 48
117 59 382 8 187 15 243 32 261 20 19 59 18 68 -3 4 -10 11 -130 16 -268 11
-288 19 -377 35 -382 18 -6 23 55 30 360 l7 288 66 55 c36 30 68 54 72 54 4 0
10 -56 14 -124z m-765 55 c6 -12 10 -129 10 -310 l0 -290 30 -31 c26 -27 30
-38 30 -90 0 -32 -4 -61 -9 -64 -13 -8 -70 60 -77 92 -10 48 -13 52 -38 62
-45 17 -50 65 -38 350 6 140 14 265 18 278 8 29 58 31 74 3z m3807 2 c-3 -5
-10 -154 -17 -334 -13 -334 -11 -369 24 -369 8 0 27 16 43 37 20 24 34 33 43
28 19 -12 6 -103 -24 -167 -16 -33 -29 -85 -35 -137 -5 -46 -14 -88 -20 -94
-8 -8 -11 29 -11 136 0 94 -4 147 -10 147 -6 0 -36 -13 -68 -29 -86 -43 -176
-73 -208 -69 -24 2 -29 8 -31 33 0 17 -2 35 -2 42 -1 6 -10 15 -21 18 -15 5
-31 -3 -60 -29 -58 -52 -98 -71 -117 -54 -31 26 0 417 44 548 25 74 49 89 153
96 134 9 151 16 179 70 14 25 47 66 75 90 46 40 75 57 63 37z m113 -110 c0
-29 -12 -63 -21 -63 -10 0 -12 63 -2 73 12 12 23 7 23 -10z m-10 -183 c0 -16
-4 -30 -10 -30 -5 0 -10 14 -10 30 0 17 5 30 10 30 6 0 10 -13 10 -30z
m-10641 -30 c28 -15 32 -57 33 -384 0 -318 -6 -381 -40 -374 -42 8 -57 127
-67 521 l-6 247 31 0 c17 0 39 -4 49 -10z m180 -27 c6 -16 18 -143 26 -283 9
-140 20 -293 25 -340 15 -131 18 -124 -37 -116 -84 11 -82 6 -89 306 -10 437
-10 426 5 443 22 27 57 21 70 -10z m144 9 c9 -9 21 -37 27 -62 14 -58 25 -543
13 -595 -11 -47 -38 -59 -52 -22 -12 31 -56 659 -48 681 8 21 40 20 60 -2z
m-486 -76 c22 -88 34 -355 23 -478 -10 -103 -18 -127 -43 -128 -17 0 -27 56
-47 262 -25 272 -26 355 -3 380 30 33 56 19 70 -36z m748 -4 c246 -81 277 -86
473 -73 92 7 228 19 303 27 105 11 161 12 250 4 63 -5 125 -10 138 -10 l23 0
-4 -262 c-4 -175 -9 -274 -18 -296 -14 -39 -71 -71 -177 -101 -37 -10 -102
-32 -143 -47 -73 -27 -82 -28 -327 -32 -162 -3 -274 0 -316 7 -97 17 -317 90
-324 107 -3 8 2 23 11 33 22 24 20 39 -14 112 -29 63 -29 67 -34 274 -3 116
-8 234 -12 264 -6 44 -4 53 7 48 8 -3 82 -28 164 -55z m9365 -49 c0 -10 -12
-58 -25 -108 l-25 -90 3 85 c3 82 17 130 37 130 6 0 10 -8 10 -17z m-334 -100
c15 -117 -26 -537 -58 -597 -10 -19 -11 -19 -22 0 -13 22 -16 591 -3 636 8 29
32 39 59 26 11 -5 19 -27 24 -65z m-6056 16 c41 -20 83 -46 93 -56 28 -31 37
-116 35 -310 -3 -162 -1 -184 15 -200 16 -16 18 -16 34 0 15 14 17 48 21 247
5 236 10 268 45 255 24 -10 34 -59 46 -214 12 -147 29 -197 58 -168 10 10 18
52 25 133 10 132 18 175 33 177 16 2 23 -49 24 -185 0 -70 5 -129 10 -132 4
-3 16 1 26 8 15 10 20 34 26 119 8 109 23 162 40 136 5 -8 7 -57 4 -109 -12
-200 51 -218 70 -20 9 96 26 137 60 146 39 10 48 -7 49 -93 1 -62 4 -78 16
-78 9 0 22 24 35 69 12 40 30 76 43 86 26 21 128 54 147 47 24 -10 48 -251 42
-419 -7 -205 -30 -288 -88 -322 -27 -16 -32 -16 -53 -3 -29 20 -126 33 -369
52 -277 22 -338 36 -354 83 -20 58 -58 133 -77 151 l-23 21 -41 -45 c-23 -25
-63 -56 -89 -68 -46 -21 -60 -22 -348 -22 l-300 0 -85 36 c-47 20 -121 51
-165 69 -112 46 -110 44 -114 76 -2 16 2 93 10 172 10 111 10 160 1 218 -13
84 -4 117 41 147 51 33 91 36 537 34 l445 -2 75 -36z m-1619 -1 c20 -48 20
-607 0 -631 -19 -24 -48 -21 -62 5 -17 31 -26 592 -10 631 16 40 54 37 72 -5z
m7827 -48 c4 -70 -26 -444 -38 -476 -6 -14 -18 -24 -30 -24 -24 0 -26 34 -10
152 6 40 14 143 18 229 5 85 12 161 16 168 4 7 15 11 24 9 13 -2 18 -17 20
-58z m-7667 1 c28 -22 28 -29 15 -286 -9 -163 0 -261 26 -292 20 -25 27 22 38
252 11 240 19 315 31 315 5 0 9 -6 9 -12 0 -7 7 -76 15 -153 8 -77 15 -204 15
-282 0 -131 -2 -143 -19 -153 -29 -15 -128 -12 -160 4 -26 14 -28 21 -34 92
-9 98 -9 447 -1 497 8 43 28 48 65 18z m281 -13 c21 -37 17 -360 -5 -480 -11
-54 -23 -98 -27 -98 -16 0 -24 116 -25 341 0 177 3 229 14 242 18 23 28 21 43
-5z m7042 -45 c-1 -7 -2 -122 -2 -255 l-2 -242 -137 -136 c-98 -95 -153 -142
-190 -159 -51 -23 -51 -23 -108 -6 -104 33 -154 83 -174 173 -22 102 -35 309
-23 350 15 49 85 130 120 138 15 4 65 26 110 50 46 24 96 44 111 44 16 0 48
14 72 30 42 29 48 30 134 28 65 -2 90 -7 89 -15z m480 -109 c10 -25 7 -210 -4
-238 -12 -34 -38 -33 -46 0 -12 48 17 254 36 254 4 0 11 -7 14 -16z m779 -91
c7 -34 -5 -98 -18 -98 -6 0 -11 18 -13 39 -6 72 19 119 31 59z m-4076 -11 c6
-4 17 -20 22 -35 14 -36 12 -251 -2 -472 l-12 -170 -111 -3 -111 -3 -6 83 c-4
46 -7 183 -7 304 0 209 1 223 21 248 27 34 58 51 71 38 6 -6 6 -69 -1 -177
-12 -184 -8 -238 18 -233 13 3 17 22 23 103 10 145 29 287 39 308 10 18 35 22
56 9z m-813 -34 c3 -13 7 -57 11 -99 6 -87 25 -161 41 -166 15 -5 24 45 24
137 0 121 24 165 66 123 16 -16 16 -40 10 -283 -7 -287 -14 -335 -51 -352 -27
-12 -110 10 -166 44 -37 22 -37 -10 1 318 11 96 20 192 20 212 0 69 33 118 44
66z m2890 -65 c4 -49 8 -180 9 -293 2 -296 -6 -308 -234 -335 -68 -8 -198 -29
-288 -47 l-163 -32 -19 23 c-23 28 -23 47 -4 216 7 72 19 195 25 274 12 149
20 181 46 181 12 0 13 -13 8 -78 -4 -46 -2 -106 5 -148 18 -105 16 -144 -9
-186 -24 -42 -23 -76 3 -92 25 -15 57 24 57 69 0 19 8 41 18 51 15 15 17 39
17 180 0 114 4 167 12 175 20 20 31 -26 39 -162 4 -68 11 -135 16 -149 9 -23
10 -24 23 -7 14 20 20 53 35 201 17 168 62 195 73 44 10 -131 45 -223 75 -193
4 3 9 68 12 143 7 184 28 217 153 241 88 18 84 21 91 -76z m-1654 59 c159 -3
282 0 350 8 l106 12 88 -36 c106 -44 133 -61 151 -95 15 -31 30 -154 45 -369
15 -226 17 -215 -26 -154 -20 29 -41 52 -45 52 -13 0 -26 -31 -32 -79 -3 -24
-14 -52 -24 -62 -29 -29 -123 -42 -308 -41 -301 0 -363 5 -419 36 -32 17 -65
26 -93 26 -33 0 -50 6 -68 25 l-25 24 0 316 c0 326 4 364 38 349 9 -4 127 -10
262 -12z m-730 -7 c27 -32 43 -545 19 -613 -15 -42 -66 -56 -178 -49 -120 9
-130 18 -133 115 -3 121 2 308 13 416 9 96 21 132 41 120 23 -14 31 -75 38
-305 9 -242 15 -303 30 -294 10 6 18 79 30 280 16 257 35 327 93 338 12 2 25
5 28 6 4 0 12 -6 19 -14z m1604 -69 c20 -26 21 -40 21 -345 0 -352 -1 -358
-59 -347 -16 4 -38 13 -47 22 -17 15 -17 19 -3 46 12 23 17 94 25 321 5 161
12 302 15 315 6 28 21 25 48 -12z m2492 -163 c-8 -189 -35 -490 -46 -519 -7
-18 -24 -26 -85 -39 -42 -10 -78 -15 -81 -13 -6 6 23 388 37 488 16 114 26
138 67 160 21 10 51 30 67 44 17 14 34 25 39 26 5 0 6 -61 2 -147z m-515 -4
c-1 -8 -9 -84 -16 -169 -7 -85 -20 -198 -29 -250 -8 -52 -18 -134 -21 -182 -8
-108 -20 -125 -123 -175 -73 -35 -83 -37 -199 -41 l-123 -4 0 103 c0 138 17
330 36 404 9 37 26 71 43 90 16 16 32 40 35 52 5 15 25 28 64 43 31 12 77 37
101 56 24 19 56 34 70 34 14 0 53 14 86 30 61 31 83 33 76 9z m240 -177 c-11
-92 -25 -221 -31 -287 -20 -221 -20 -224 -38 -229 -9 -3 -50 -8 -89 -12 -61
-5 -73 -4 -73 8 0 8 11 140 25 294 33 370 47 446 77 403 13 -19 2 -166 -23
-297 -27 -145 -49 -296 -49 -329 1 -101 78 -38 108 87 10 44 28 171 56 402 16
127 31 174 48 144 6 -11 2 -74 -11 -184z m492 138 c-7 -28 -13 -25 -13 6 0 14
4 23 9 20 5 -3 7 -15 4 -26z m-8928 -99 c22 -5 204 -15 405 -21 398 -11 436
-14 445 -39 14 -35 26 -432 18 -586 l-8 -159 -110 -46 -110 -45 -212 -3 c-157
-3 -240 0 -320 12 -60 9 -129 16 -154 16 -33 0 -51 6 -64 20 -10 11 -33 22
-52 26 -45 9 -61 36 -73 125 -12 96 -13 480 -1 513 6 13 25 44 43 68 18 25 48
66 66 92 36 52 33 52 127 27z m1734 -155 c31 -37 43 -150 46 -458 4 -386 16
-358 -155 -358 -142 0 -330 23 -411 50 -32 11 -85 20 -118 20 -83 0 -106 26
-135 154 -21 90 -20 104 10 170 25 55 25 61 22 233 -3 155 -1 178 13 181 41 8
64 -158 55 -390 -7 -161 -2 -183 28 -135 23 36 36 154 36 329 0 90 4 170 10
178 7 11 15 12 37 4 51 -17 55 -39 49 -260 -5 -218 0 -317 18 -311 19 6 34 93
51 295 9 108 20 212 23 231 9 42 34 61 60 45 15 -10 18 -36 22 -225 5 -206 13
-259 40 -259 18 0 22 25 29 228 8 221 16 258 58 268 13 4 26 4 29 1 14 -14 24
-92 24 -199 0 -64 4 -119 8 -122 5 -3 17 9 27 26 13 23 21 66 28 162 5 72 11
137 13 144 5 16 69 15 83 -2z m399 -31 c103 -8 291 -20 417 -25 127 -6 252
-13 280 -17 32 -3 74 1 120 12 l70 18 40 -26 c89 -58 79 -25 85 -308 4 -186 9
-260 18 -278 9 -15 17 -20 24 -13 5 5 14 90 19 188 12 223 24 333 38 347 7 7
18 7 37 -2 25 -11 26 -14 20 -64 -22 -178 -24 -413 -3 -433 18 -18 25 20 36
180 12 173 24 250 43 273 15 19 85 17 96 -3 5 -9 7 -67 5 -128 -5 -121 8 -190
29 -152 6 11 14 68 18 128 4 59 13 114 19 122 16 19 96 50 109 42 5 -3 12 -30
14 -61 4 -53 30 -101 49 -90 4 3 11 30 14 60 4 30 12 61 19 70 11 15 171 88
218 100 20 5 27 0 41 -30 24 -50 57 -61 232 -75 83 -6 170 -16 195 -22 25 -5
137 -13 250 -17 229 -8 259 -16 279 -72 16 -46 14 -137 -8 -349 -29 -275 -39
-309 -110 -373 l-41 -37 -183 0 c-228 0 -382 14 -450 41 -29 12 -85 34 -126
49 -40 16 -82 37 -93 47 -36 33 -57 90 -68 183 -10 90 -22 130 -39 130 -18 0
-39 -78 -46 -170 -8 -112 -20 -138 -73 -168 -36 -20 -53 -22 -178 -22 -164 0
-395 23 -450 44 -22 8 -57 15 -79 15 -84 -1 -121 11 -132 41 -14 37 -23 38
-378 45 -282 6 -388 16 -431 38 -12 7 -36 31 -53 55 -24 33 -37 42 -60 42 -64
0 -72 32 -101 380 -27 332 -30 300 31 300 28 0 134 -7 237 -15z m6661 -52 c0
-36 -2 -43 -9 -27 -7 18 -3 74 6 74 2 0 4 -21 3 -47z m-1436 -96 c3 -8 1 -47
-3 -88 -5 -41 -13 -148 -19 -239 -14 -208 -29 -331 -43 -347 -27 -33 -31 4
-24 246 7 271 18 382 41 418 17 26 40 30 48 10z m145 -75 c2 -37 -3 -128 -11
-202 -9 -74 -19 -166 -22 -205 -7 -85 -23 -137 -38 -132 -35 11 -2 568 36 615
18 23 31 -5 35 -76z m-413 33 c-3 -14 -7 -35 -10 -48 -2 -13 -11 -121 -20
-241 -8 -120 -20 -227 -26 -238 -9 -16 -17 -19 -41 -14 -28 6 -30 4 -24 -20
10 -37 -18 -122 -57 -177 -39 -55 -121 -120 -187 -149 -48 -21 -273 -68 -325
-68 -56 0 -216 39 -253 62 -19 12 -63 32 -96 44 l-61 22 1 53 c1 30 6 170 11
312 11 323 -3 301 194 315 115 8 141 13 166 31 59 44 276 83 472 85 l116 1 -3
-34 c-4 -37 25 -178 38 -186 18 -11 33 69 27 146 -6 77 -6 78 24 103 40 34 61
34 54 1z m109 -11 c14 -36 -5 -240 -25 -274 -10 -17 -12 231 -3 268 7 26 19
29 28 6z m-2104 -172 c3 -12 2 -93 -2 -180 -15 -365 -7 -518 29 -529 16 -6 21
42 33 297 14 306 21 350 55 350 9 0 24 -15 35 -32 17 -28 20 -51 20 -161 0
-192 -20 -520 -33 -535 -13 -17 -180 -13 -197 4 -40 40 -34 741 6 806 11 17
48 3 54 -20z m831 -14 c17 -59 13 -387 -6 -567 -8 -79 -15 -162 -15 -185 0
-58 -27 -81 -112 -96 -77 -14 -174 -9 -273 14 -38 8 -101 15 -140 16 -95 0
-99 5 -87 71 19 97 22 141 32 391 9 261 15 294 57 309 41 15 48 -18 47 -206 0
-115 -6 -199 -17 -260 -27 -145 -22 -184 22 -170 33 10 42 54 52 259 11 245
24 373 40 392 15 19 35 18 43 -2 12 -31 6 -301 -10 -429 -30 -259 -31 -265
-18 -276 20 -17 44 18 44 65 0 23 6 89 14 147 8 57 20 167 26 244 13 175 28
261 46 276 34 28 38 -3 45 -349 4 -185 8 -343 8 -349 2 -23 28 -14 41 15 10
20 15 113 20 352 3 179 9 331 14 338 11 17 64 41 93 42 18 0 25 -9 34 -42z
m2759 -45 c0 -142 -37 -488 -61 -581 -22 -80 -166 -178 -319 -216 -95 -24
-115 -34 -149 -72 -18 -19 -35 -34 -40 -34 -16 0 -24 146 -17 303 16 355 32
483 62 508 8 6 84 20 170 30 165 19 240 44 309 103 17 14 33 25 38 26 4 0 7
-30 7 -67z m-1091 -66 c-1 -51 -4 -105 -8 -122 -10 -43 -25 132 -16 185 12 69
25 33 24 -63z m-8249 -190 c16 -20 47 -34 123 -57 128 -39 271 -108 318 -155
42 -40 52 -73 70 -225 13 -115 7 -276 -12 -329 -8 -22 -22 -38 -44 -47 -75
-31 -379 -49 -591 -35 -200 14 -250 25 -268 59 -23 47 -49 213 -51 332 0 63
-5 124 -11 135 -5 11 -9 61 -9 110 0 69 4 96 17 114 11 15 15 34 11 52 -7 35
14 39 40 8 14 -15 27 -19 59 -17 36 3 42 6 52 38 14 44 34 50 168 46 103 -2
108 -4 128 -29z m8360 -32 c10 -12 11 -89 6 -353 -8 -393 -9 -408 -39 -416
-12 -3 -61 -43 -108 -89 -101 -98 -228 -186 -337 -232 -42 -18 -112 -51 -155
-74 -69 -37 -117 -51 -117 -36 0 15 43 88 81 135 30 38 42 63 42 85 0 16 -6
116 -11 220 -6 105 -11 247 -12 317 l0 127 218 110 c119 61 260 135 312 165
52 30 98 55 101 55 4 1 12 -6 19 -14z m320 -21 c9 -27 9 -58 1 -132 -7 -53
-18 -180 -26 -281 -7 -101 -18 -188 -23 -193 -7 -7 -15 -2 -23 13 -22 42 9
575 36 619 12 19 23 10 35 -26z m-158 -10 c19 -41 -12 -563 -38 -648 -6 -18
-7 -18 -17 -1 -6 11 -10 140 -11 330 -1 251 2 315 13 328 19 24 40 21 53 -9z
m-9573 -176 c52 -169 80 -240 94 -235 23 7 24 60 5 168 -10 62 -18 131 -16
153 3 39 4 41 41 44 45 4 51 -8 73 -142 16 -96 45 -176 65 -176 12 0 8 143 -7
276 l-6 50 63 -6 c91 -10 107 -17 128 -58 38 -75 52 -145 57 -277 2 -71 6
-178 8 -237 l4 -108 -37 -6 c-20 -4 -61 -16 -91 -26 -74 -27 -170 -37 -257
-27 -96 11 -130 29 -156 87 -21 44 -22 64 -25 355 -2 169 1 307 5 307 5 0 28
-64 52 -142z m2850 117 c16 -8 68 -19 117 -25 219 -25 380 -42 524 -55 176
-16 192 -19 277 -46 l61 -20 -4 -52 c-2 -29 -9 -167 -14 -307 -13 -309 -26
-470 -39 -483 -6 -6 -21 0 -42 17 -17 15 -45 30 -61 33 -48 9 -50 31 -39 318
10 250 4 455 -14 485 -18 29 -25 -38 -35 -362 -11 -335 -20 -436 -42 -449 -19
-12 -230 -24 -418 -24 -159 0 -182 2 -249 24 -100 33 -188 93 -226 155 l-30
49 0 315 c0 299 1 314 19 328 11 8 20 24 21 35 4 55 131 97 194 64z m-1177
-105 c5 -25 17 -191 28 -370 20 -327 26 -390 40 -390 15 0 22 97 30 380 4 157
10 293 15 303 6 14 17 17 49 15 40 -3 41 -4 48 -48 4 -25 11 -175 15 -335 5
-159 12 -296 17 -303 6 -10 11 -11 20 -2 7 7 13 111 16 330 3 175 8 321 12
325 4 4 36 10 72 12 l66 6 0 -41 c0 -76 40 -61 60 23 22 87 60 122 80 71 5
-15 17 -126 25 -249 24 -346 41 -467 65 -467 24 0 24 315 -1 443 -10 54 -12
269 -3 303 5 19 16 23 72 29 37 3 74 8 82 10 8 3 22 0 30 -5 13 -8 12 -13 -4
-40 -17 -29 -17 -33 -2 -49 24 -26 36 -117 36 -267 0 -249 -29 -438 -76 -484
-21 -21 -33 -25 -82 -24 -31 1 -79 6 -107 12 -27 6 -111 11 -185 12 -175 1
-390 16 -401 27 -16 16 -28 115 -49 417 -12 160 -25 307 -29 326 -11 49 3 82
31 78 18 -2 24 -12 30 -48z m3164 -69 c44 -11 120 -24 169 -30 127 -15 423
-32 515 -30 70 2 81 -1 112 -25 39 -29 107 -107 128 -146 11 -19 14 -86 16
-280 1 -249 7 -326 24 -315 11 7 19 91 30 335 5 113 12 213 15 222 4 9 14 19
23 23 33 12 42 -23 43 -164 1 -140 10 -245 22 -257 18 -18 22 21 19 192 -2
101 -1 193 3 204 9 27 47 26 81 -4 28 -23 28 -24 34 -185 7 -164 15 -196 43
-173 10 9 16 51 21 155 10 194 8 189 65 185 l46 -3 11 -121 c11 -115 24 -164
44 -164 17 0 23 73 16 200 l-7 127 79 5 c60 3 85 10 108 27 38 28 129 31 167
7 31 -21 101 -41 212 -61 50 -9 126 -26 170 -37 64 -15 126 -20 315 -24 l235
-4 6 -38 c4 -20 10 -107 13 -192 11 -229 41 -439 66 -455 17 -11 22 105 11
305 -7 112 -14 207 -16 211 -24 38 117 181 148 150 8 -8 14 -128 19 -369 7
-367 13 -462 28 -462 17 0 31 40 30 83 0 23 -3 143 -6 266 -5 239 6 442 27
467 7 9 25 14 43 12 l31 -3 6 -395 c5 -372 9 -430 30 -430 26 0 33 49 29 190
-3 80 -5 247 -5 372 0 208 2 228 19 247 11 12 29 21 41 21 48 0 50 -10 56
-389 6 -346 11 -401 39 -401 21 0 25 65 25 422 0 409 -1 402 55 410 23 3 25
-1 33 -52 11 -70 1 -619 -13 -684 -7 -34 -7 -53 0 -60 30 -30 45 45 60 304 30
531 29 522 102 578 41 32 62 39 72 24 2 -4 1 -136 -4 -292 -6 -229 -12 -311
-31 -417 -13 -72 -20 -135 -16 -140 5 -4 26 2 48 14 21 12 39 18 39 14 0 -4
-15 -31 -33 -61 -71 -120 -236 -167 -599 -173 -173 -3 -190 4 -229 90 -32 72
-50 78 -119 43 -51 -26 -135 -26 -385 0 -336 35 -570 71 -590 91 -6 6 -14 145
-20 354 -5 189 -13 346 -16 350 -20 19 -32 -77 -36 -288 l-6 -228 -76 -65
c-42 -35 -86 -67 -98 -70 -12 -3 -24 -12 -27 -20 -9 -24 67 -97 179 -173 135
-93 155 -116 156 -189 4 -225 15 -472 22 -478 4 -4 13 -2 20 5 9 9 13 86 17
276 3 145 8 267 13 271 13 13 59 -9 81 -39 19 -26 21 -46 27 -305 7 -277 14
-372 29 -372 17 0 20 43 21 323 1 161 4 306 8 322 6 26 11 30 36 28 68 -5 81
-82 49 -299 -10 -72 -14 -138 -9 -196 6 -87 22 -155 34 -143 3 4 7 75 7 158 2
149 21 316 46 393 16 50 53 60 68 19 6 -16 10 -159 10 -351 l0 -326 -72 5
c-40 2 -109 10 -153 16 -44 7 -99 14 -122 17 -54 6 -104 40 -125 85 -27 57
-38 62 -105 49 -32 -7 -104 -30 -159 -51 -127 -50 -166 -54 -339 -40 -253 21
-327 46 -428 140 -63 58 -73 77 -52 97 22 22 10 34 -37 34 -38 0 -40 2 -32 22
4 13 8 153 9 312 2 329 -9 295 103 327 80 23 93 23 347 11 185 -9 231 -9 293
4 32 6 30 49 -5 71 -24 16 -63 19 -344 23 l-317 5 -26 28 c-29 31 -45 32 -141
11 -201 -43 -229 -46 -370 -41 -201 7 -357 52 -424 121 l-32 33 -10 179 c-13
198 -6 514 11 560 6 16 23 41 38 57 31 33 59 34 168 8z m-302 -17 c10 -25 6
-598 -4 -714 -8 -90 -13 -108 -32 -123 -16 -13 -31 -17 -53 -13 -16 4 -132 10
-257 15 -246 10 -283 18 -309 68 -11 21 -14 92 -14 347 0 177 4 333 8 349 6
22 13 27 37 27 58 0 63 -22 80 -375 5 -104 12 -198 16 -208 16 -44 24 18 25
208 1 252 7 299 35 303 13 2 24 -4 29 -15 4 -10 9 -110 11 -223 5 -238 11
-297 26 -282 7 7 11 98 12 251 1 227 2 241 22 268 12 15 33 35 47 43 24 13 28
13 44 -3 16 -16 18 -41 18 -242 0 -156 3 -227 12 -236 13 -13 16 17 34 266 15
217 31 275 66 230 13 -17 14 -49 8 -215 -7 -191 -1 -290 16 -290 13 0 19 52
28 270 9 209 16 248 53 288 25 26 34 27 42 6z m4146 -138 c0 -24 -9 -101 -20
-172 -10 -71 -29 -219 -40 -329 -11 -110 -25 -215 -31 -233 -12 -37 -35 -38
-32 -1 1 13 6 78 12 144 6 66 15 197 21 290 6 94 17 201 25 239 24 113 65 152
65 62z m-8815 -138 c21 -52 32 -148 18 -148 -10 0 -36 80 -49 153 -9 51 10 48
31 -5z m10053 -463 l-3 -344 -36 -38 c-54 -58 -103 -130 -161 -239 -51 -95
-54 -99 -93 -105 -22 -4 -84 -5 -139 -2 l-98 6 4 181 c3 101 10 193 16 209 7
17 24 32 42 38 38 14 43 41 44 214 l1 130 -37 -3 c-21 -2 -38 -1 -38 2 0 3 34
30 76 61 52 39 80 68 91 93 8 20 22 42 32 49 58 47 184 90 264 92 l38 1 -3
-345z m180 278 c6 -9 12 -40 12 -67 1 -372 6 -494 19 -510 23 -27 64 -17 74
19 23 78 105 194 127 180 19 -12 11 -84 -14 -123 -25 -40 -141 -154 -193 -190
-17 -12 -46 -22 -66 -22 l-34 0 -7 130 c-7 124 1 569 10 599 5 17 54 6 72 -16z
m141 -158 c-12 -162 -20 -224 -30 -230 -28 -18 -23 314 6 380 l18 40 8 -35 c4
-19 3 -89 -2 -155z m208 18 c-3 -10 -5 -2 -5 17 0 19 2 27 5 18 2 -10 2 -26 0
-35z m-10267 -13 c274 -16 324 -24 411 -64 41 -19 97 -39 124 -43 41 -7 50
-12 56 -35 7 -27 24 -688 18 -688 -2 0 -29 7 -59 15 -84 23 -109 19 -169 -22
-49 -34 -61 -38 -113 -36 -110 3 -350 56 -435 95 -51 24 -66 53 -88 178 -10
52 -24 111 -32 130 -18 45 -43 232 -43 326 0 70 1 72 29 82 22 8 30 18 35 47
7 36 8 37 44 32 20 -3 120 -11 222 -17z m8550 -37 c-1 -14 -30 -47 -36 -41 -8
8 14 48 26 48 6 0 10 -3 10 -7z m640 -81 c0 -16 -4 -53 -10 -83 -5 -30 -19
-173 -30 -319 -20 -260 -33 -377 -47 -429 -6 -22 -8 -19 -14 24 -6 49 2 243
21 500 6 72 10 173 10 226 0 53 3 99 7 102 3 4 19 7 35 7 24 0 28 -4 28 -28z
m-8416 -71 c17 -29 20 -64 27 -279 9 -249 23 -350 54 -382 30 -29 37 1 26 101
-12 98 -30 413 -31 511 l0 57 46 7 c33 5 48 3 54 -6 4 -7 11 -101 15 -209 4
-108 15 -272 24 -364 l17 -169 -172 4 c-95 1 -176 7 -180 11 -5 4 5 18 21 31
l29 24 -12 58 c-16 82 -30 254 -38 487 l-7 198 54 -24 c36 -16 60 -34 73 -56z
m501 41 l70 -28 0 -414 0 -415 -27 -3 c-20 -2 -38 7 -69 35 -66 62 -70 91 -76
501 -6 405 -17 370 102 324z m-179 -14 c5 -7 13 -146 20 -308 7 -163 15 -330
19 -372 7 -73 6 -76 -15 -83 -16 -5 -28 -1 -43 14 -19 19 -20 22 -4 38 14 17
15 23 3 58 -18 52 -45 304 -53 491 -6 136 -5 148 12 160 23 17 51 18 61 2z
m579 -43 c61 -7 225 -19 365 -25 298 -12 407 -24 431 -45 35 -32 67 -190 78
-378 4 -76 13 -143 19 -149 6 -6 18 -8 28 -4 16 6 17 20 11 155 -4 81 -12 182
-17 225 -8 56 -8 81 0 91 14 16 48 8 60 -15 6 -10 14 -97 19 -193 7 -120 15
-187 26 -213 29 -69 38 -22 33 161 -6 200 -3 227 26 223 32 -5 45 -60 56 -251
12 -189 16 -217 30 -217 25 0 32 59 28 215 -3 88 -2 176 1 195 6 32 9 35 41
35 41 0 42 -5 59 -195 16 -169 21 -200 37 -200 18 0 22 34 33 245 8 167 10
180 31 197 18 15 57 25 61 16 1 -2 5 -103 8 -225 4 -123 11 -226 15 -230 4 -4
15 -2 24 4 15 9 17 33 17 214 0 214 5 241 44 252 41 10 46 -13 51 -269 5 -224
12 -291 27 -236 8 31 31 28 40 -5 10 -35 9 -129 -1 -167 l-7 -29 -136 6 c-75
4 -243 7 -374 7 l-238 0 -7 45 c-9 62 -30 75 -120 75 -126 0 -184 -33 -184
-107 l0 -43 -42 6 c-76 11 -113 23 -148 49 -18 13 -50 27 -69 30 -76 13 -80
14 -83 42 -3 26 -7 28 -78 39 -41 7 -100 21 -130 32 -30 11 -77 23 -104 27
-34 5 -58 17 -80 37 -35 34 -48 33 97 2 65 -14 87 -15 131 -5 59 13 119 62 99
82 -6 6 -81 14 -168 19 -174 10 -257 24 -275 45 -14 17 -28 161 -31 310 l-2
110 44 11 c60 16 96 16 224 -1z m-2084 -22 c15 -40 51 -290 47 -332 l-3 -36
-17 25 c-12 18 -22 70 -32 165 -8 77 -18 155 -22 173 -8 40 12 44 27 5z m4637
-94 c51 -5 108 -15 125 -23 51 -22 104 -78 116 -122 13 -50 31 -280 31 -406
l0 -98 -107 -108 c-92 -92 -116 -110 -162 -125 -66 -21 -96 -21 -152 -2 -24 9
-119 25 -210 35 -92 11 -184 26 -205 34 -22 7 -86 46 -143 86 l-103 74 6 45
c9 67 20 74 99 61 145 -23 227 -12 227 29 0 23 -26 29 -195 41 -69 5 -132 13
-139 19 -14 12 -29 79 -20 92 2 4 19 0 37 -11 39 -22 122 -40 198 -41 67 -2
79 4 79 36 0 55 -1 55 -161 55 l-149 0 0 168 c0 93 3 172 7 175 9 10 707 -2
821 -14z m4794 -114 c-3 -130 -30 -410 -47 -504 -16 -79 -43 -136 -66 -136 -8
0 -13 5 -12 10 10 67 24 241 33 417 6 120 15 232 20 249 12 40 39 71 59 67 13
-3 16 -20 13 -103z m-355 -200 c-8 -255 -25 -478 -41 -524 -13 -39 -32 -51
-40 -24 -3 10 -7 153 -8 318 -3 294 -2 301 20 340 12 22 22 53 22 70 0 40 18
59 38 42 13 -10 14 -44 9 -222z m167 169 c12 -31 6 -335 -9 -450 -17 -125 -52
-245 -74 -252 -11 -4 -12 37 -6 250 5 190 4 268 -5 310 -7 31 -10 68 -6 83 14
56 85 98 100 59z m-4208 -22 c6 -4 16 -16 22 -28 17 -33 34 -258 31 -406 -3
-114 -2 -135 9 -124 22 22 31 83 41 283 10 189 21 248 43 241 20 -7 25 -46 32
-283 8 -254 21 -354 41 -333 3 3 9 132 14 287 9 300 11 311 60 311 54 0 55 -3
62 -222 9 -242 26 -368 50 -368 19 0 22 64 11 279 -6 124 -8 238 -6 252 7 31
40 49 88 49 23 0 37 -6 42 -17 4 -10 10 -136 13 -280 6 -240 10 -287 27 -270
4 3 10 84 15 179 19 352 31 428 71 436 28 5 32 -52 25 -372 -5 -244 -9 -303
-22 -322 -17 -27 -131 -88 -146 -79 -5 4 -18 24 -29 46 -13 25 -29 41 -46 45
-14 4 -90 -4 -169 -16 -79 -12 -154 -20 -167 -17 -29 8 -144 101 -160 130 -13
25 -32 482 -22 560 5 43 8 47 32 47 15 0 32 -4 38 -8z m3813 -160 c40 -29 51
-63 61 -184 11 -128 13 -468 4 -493 -4 -9 -25 -25 -48 -37 -22 -11 -72 -45
-109 -75 -37 -30 -87 -63 -110 -74 -23 -11 -57 -31 -74 -44 -63 -45 -239 -58
-393 -29 -112 20 -170 49 -192 94 -12 23 -27 37 -44 40 -15 3 -36 18 -48 33
-20 27 -21 42 -24 287 -3 236 6 422 22 437 3 4 37 0 74 -8 89 -19 341 -16 497
6 69 9 154 29 200 45 90 33 142 33 184 2z m-1053 -56 c18 -14 19 -29 19 -328
0 -271 -2 -318 -17 -350 -11 -24 -26 -40 -41 -43 -34 -9 -67 10 -67 39 0 43
-13 35 -54 -32 -44 -71 -66 -84 -96 -57 -10 9 -31 15 -47 14 -50 -6 -53 2 -53
139 1 182 17 463 30 511 6 22 19 45 30 51 30 16 77 12 85 -7 4 -10 10 -138 14
-285 7 -248 11 -291 27 -275 10 10 23 212 23 374 1 116 5 162 17 193 24 61 86
87 130 56z m-5597 -256 c11 -7 0 -9 -41 -10 -62 0 -88 7 -88 22 0 13 104 3
129 -12z"
                    />
                    <path
                      d="M3628 8273 c-43 -11 -12 -23 60 -23 72 1 75 2 52 15 -24 14 -76 18
-112 8z"
                    />
                    <path
                      d="M5443 8193 c-72 -5 -83 -19 -20 -27 151 -19 287 -17 287 4 0 17 -154
30 -267 23z"
                    />
                    <path
                      d="M6307 8143 c-15 -14 -5 -25 36 -43 48 -21 223 -54 238 -44 23 14 -24
43 -82 50 -38 4 -64 13 -75 25 -12 13 -31 19 -64 19 -26 0 -50 -3 -53 -7z"
                    />
                    <path
                      d="M8238 7972 c-67 -8 -11 -35 110 -52 40 -5 84 -8 98 -5 13 3 35 7 49
10 14 3 25 9 25 14 0 16 -217 42 -282 33z"
                    />
                    <path
                      d="M7216 8001 c-11 -17 35 -30 87 -24 29 4 65 0 96 -11 61 -20 85 -20
100 -2 10 12 8 17 -11 28 -30 17 -263 25 -272 9z"
                    />
                    <path
                      d="M9205 7930 c-14 -22 118 -71 228 -85 45 -6 62 -4 83 10 38 24 16 41
-64 49 -37 4 -95 13 -130 21 -73 17 -108 19 -117 5z"
                    />
                    <path
                      d="M5323 7623 c-13 -30 -18 -83 -21 -260 -2 -123 -1 -235 2 -250 6 -22
9 -24 21 -12 40 40 62 559 24 559 -6 0 -17 -17 -26 -37z"
                    />
                    <path
                      d="M8864 7318 c-14 -20 -18 -537 -5 -571 9 -24 14 -26 31 -17 34 18 45
83 44 260 -1 111 -7 189 -18 240 -17 79 -37 113 -52 88z"
                    />
                    <path
                      d="M11150 6217 c-171 -65 -266 -135 -232 -169 9 -9 27 -8 84 5 40 10 79
17 86 17 7 0 -25 -30 -70 -67 -92 -74 -107 -90 -108 -115 0 -29 28 -21 157 41
134 65 164 94 145 136 -9 19 -19 25 -42 25 -45 0 -37 14 28 48 68 36 95 63 90
89 -5 29 -45 26 -138 -10z"
                    />
                    <path
                      d="M11096 5850 c-85 -42 -129 -78 -153 -124 l-15 -29 33 7 c19 3 85 31
146 61 88 43 114 60 119 79 14 58 -24 60 -130 6z"
                    />
                    <path
                      d="M3604 6105 c-9 -23 9 -38 68 -60 43 -16 77 -20 183 -19 142 1 205 15
173 38 -11 8 -64 15 -135 19 -68 3 -137 12 -162 21 -57 21 -120 21 -127 1z"
                    />
                    <path
                      d="M3510 5945 c-24 -29 -11 -34 229 -100 105 -29 140 -35 165 -28 46 13
38 32 -24 55 -30 11 -80 29 -110 40 -65 24 -173 48 -216 48 -17 0 -37 -7 -44
-15z"
                    />
                    <path
                      d="M3567 5762 c-21 -8 -36 -20 -34 -26 15 -49 409 -76 424 -29 5 13 -60
27 -199 43 -54 6 -110 15 -126 19 -18 6 -40 3 -65 -7z"
                    />
                    <path
                      d="M3606 5552 c-37 -9 -44 -27 -15 -43 16 -8 55 -10 124 -6 107 5 156
20 136 40 -13 13 -199 20 -245 9z"
                    />
                    <path
                      d="M5665 5910 c-10 -11 -16 -22 -14 -25 3 -2 38 -11 79 -20 161 -34 338
-28 287 9 -16 12 -264 56 -313 56 -11 0 -29 -9 -39 -20z"
                    />
                    <path
                      d="M5588 5779 c-45 -25 1 -69 104 -100 106 -33 280 -17 242 21 -5 5 -54
16 -109 25 -55 9 -123 27 -152 41 -58 27 -61 27 -85 13z"
                    />
                    <path
                      d="M5651 5601 c-19 -3 -36 -11 -38 -17 -6 -19 182 -84 242 -84 24 0 45
18 45 37 0 20 -71 51 -141 63 -35 5 -66 9 -69 9 -3 -1 -20 -4 -39 -8z"
                    />
                    <path
                      d="M7580 5748 c-15 -8 -26 -24 -28 -40 -3 -24 1 -28 36 -37 69 -19 393
-42 409 -30 20 18 15 47 -11 64 -53 35 -360 67 -406 43z"
                    />
                    <path
                      d="M7592 5548 c-19 -22 -21 -27 -8 -37 50 -42 396 -38 355 4 -13 13
-172 46 -244 52 -76 5 -81 4 -103 -19z"
                    />
                    <path
                      d="M7710 5412 c-31 -12 -46 -24 -48 -38 -4 -28 19 -34 139 -34 108 0
139 12 120 47 -23 44 -129 56 -211 25z"
                    />
                    <path
                      d="M8848 5737 c-51 -43 -62 -81 -66 -226 -6 -191 20 -475 42 -468 7 2
12 47 13 128 5 231 22 525 33 553 14 37 10 40 -22 13z"
                    />
                    <path
                      d="M11362 5388 c2 -12 11 -24 21 -26 14 -3 18 1 15 20 -2 12 -11 24 -21
26 -14 3 -18 -1 -15 -20z"
                    />
                    <path
                      d="M474 5336 c-11 -30 3 -117 23 -144 17 -23 29 -27 77 -30 54 -3 56 -2
56 23 0 18 -9 31 -30 43 -16 9 -30 25 -30 34 0 16 12 22 63 30 58 9 -11 50
-95 56 -47 4 -59 1 -64 -12z"
                    />
                    <path
                      d="M483 5073 c-9 -23 32 -53 73 -53 45 0 57 26 25 52 -29 23 -89 24 -98
1z"
                    />
                    <path
                      d="M523 4933 c-21 -8 -15 -33 11 -44 50 -23 143 16 97 40 -20 11 -86 14
-108 4z"
                    />
                    <path
                      d="M565 4834 c-24 -24 -13 -39 32 -42 27 -2 44 2 53 13 30 36 -50 64
-85 29z"
                    />
                    <path
                      d="M10564 5109 c-17 -5 -43 -20 -58 -34 -24 -22 -26 -28 -15 -41 32 -39
159 11 159 62 0 20 -5 24 -27 23 -16 0 -42 -5 -59 -10z"
                    />
                    <path
                      d="M10498 4929 c-24 -13 -23 -46 1 -59 26 -13 84 -13 116 2 30 13 33 43
6 58 -25 13 -101 13 -123 -1z"
                    />
                    <path
                      d="M10506 4734 c-9 -22 21 -73 51 -87 21 -9 27 -8 38 6 15 20 9 54 -13
80 -19 21 -68 23 -76 1z"
                    />
                    <path
                      d="M2460 5165 c0 -40 121 -74 263 -75 66 0 88 3 85 12 -6 16 -64 33
-191 59 -119 23 -157 24 -157 4z"
                    />
                    <path
                      d="M2518 5015 c-5 -5 -8 -12 -8 -16 0 -12 87 -37 193 -55 85 -15 100
-16 108 -3 13 21 6 32 -34 52 -39 20 -245 37 -259 22z"
                    />
                    <path
                      d="M2502 4808 c8 -23 120 -50 234 -55 115 -6 143 2 113 32 -30 29 -356
50 -347 23z"
                    />
                    <path
                      d="M4596 5101 c-8 -13 57 -37 192 -71 126 -33 153 -35 183 -20 31 17 22
27 -46 50 -120 40 -315 64 -329 41z"
                    />
                    <path
                      d="M4605 4970 c-3 -5 -4 -14 -1 -19 8 -13 128 -40 223 -50 125 -13 171
3 105 38 -60 32 -312 56 -327 31z"
                    />
                    <path
                      d="M4608 4803 c-47 -12 3 -40 119 -67 53 -12 78 -13 107 -5 56 16 45 41
-24 55 -70 14 -180 23 -202 17z"
                    />
                    <path
                      d="M4685 4641 c-41 -10 -75 -29 -75 -43 0 -8 30 -9 101 -4 102 7 149 18
149 35 0 20 -110 27 -175 12z"
                    />
                    <path
                      d="M5658 4040 c-33 -20 -21 -46 31 -69 56 -24 100 -26 169 -10 82 20 93
50 24 68 -61 16 -205 23 -224 11z"
                    />
                    <path
                      d="M5700 3858 c-43 -23 -30 -41 49 -67 67 -22 76 -23 100 -10 34 19 34
50 -1 67 -42 20 -119 25 -148 10z"
                    />
                    <path
                      d="M5715 3678 c-45 -26 -40 -48 11 -48 46 0 104 25 104 44 0 12 -11 16
-47 16 -27 0 -57 -6 -68 -12z"
                    />
                    <path
                      d="M6459 4944 c-11 -14 -9 -19 11 -34 14 -10 34 -21 45 -24 11 -2 38 -9
60 -15 109 -30 263 -32 272 -4 7 20 -265 93 -345 93 -16 0 -36 -7 -43 -16z"
                    />
                    <path
                      d="M6524 4735 c-9 -23 2 -43 37 -64 36 -22 172 -61 213 -61 31 0 76 21
96 45 18 21 -1 35 -49 35 -19 0 -76 14 -128 30 -104 33 -160 38 -169 15z"
                    />
                    <path
                      d="M6582 4493 c5 -24 69 -54 133 -60 52 -6 57 -4 54 13 -6 28 -84 64
-142 64 -40 0 -48 -3 -45 -17z"
                    />
                    <path
                      d="M6647 4369 c-17 -10 -17 -12 -2 -29 30 -33 145 -38 145 -6 0 28 -111
55 -143 35z"
                    />
                    <path
                      d="M8555 4730 c-17 -19 -17 -21 0 -46 l17 -27 172 6 c163 6 239 17 251
37 16 26 -132 50 -311 50 -98 0 -113 -2 -129 -20z"
                    />
                    <path
                      d="M8708 4570 c-51 -9 -68 -16 -68 -30 0 -17 74 -30 207 -36 l133 -7 0
26 c0 17 -8 31 -22 38 -28 15 -187 21 -250 9z"
                    />
                    <path
                      d="M8651 4387 c-40 -14 -50 -31 -24 -42 10 -4 79 -13 153 -20 132 -12
135 -12 153 8 10 11 17 30 15 41 -3 20 -10 21 -133 23 -81 1 -143 -2 -164 -10z"
                    />
                    <path
                      d="M8773 4210 c-27 -11 -30 -46 -5 -52 24 -6 141 18 159 34 13 11 13 13
-4 19 -26 11 -124 10 -150 -1z"
                    />
                    <path
                      d="M11043 4524 c-40 -17 20 -74 76 -74 52 1 123 35 119 57 -3 15 -16 18
-93 20 -50 1 -96 -1 -102 -3z"
                    />
                    <path
                      d="M11073 4383 c-26 -5 -54 -39 -47 -58 6 -16 48 -35 77 -35 49 0 86 52
55 77 -17 13 -58 21 -85 16z"
                    />
                    <path
                      d="M11039 4220 l-24 -20 24 -20 c28 -24 93 -27 115 -5 46 47 -63 89
-115 45z"
                    />
                    <path
                      d="M1503 4230 c-51 -21 -10 -49 119 -80 105 -25 176 -26 185 -3 8 21
-35 58 -85 73 -52 16 -192 22 -219 10z"
                    />
                    <path
                      d="M1512 4048 c5 -36 120 -90 194 -92 109 -2 70 46 -75 93 -35 12 -77
21 -93 21 -25 0 -29 -4 -26 -22z"
                    />
                    <path
                      d="M1570 3835 c0 -8 18 -24 40 -35 35 -18 40 -25 40 -54 0 -20 6 -36 14
-39 23 -9 88 10 110 32 28 28 16 46 -55 82 -62 33 -149 41 -149 14z"
                    />
                    <path
                      d="M3610 4236 c0 -49 30 -68 111 -73 72 -5 159 9 159 26 0 21 -162 71
-227 71 -39 0 -43 -2 -43 -24z"
                    />
                    <path
                      d="M3640 4087 c-42 -14 -47 -45 -10 -65 29 -15 165 -10 218 8 42 16 43
44 1 59 -42 14 -164 13 -209 -2z"
                    />
                    <path
                      d="M3644 3895 c-18 -18 -23 -30 -16 -37 16 -16 94 -29 134 -23 51 9 63
26 40 55 -14 18 -32 24 -75 28 -50 4 -58 2 -83 -23z"
                    />
                    <path
                      d="M3694 3748 c-17 -4 -37 -15 -44 -23 -10 -13 -7 -17 23 -30 47 -19 82
-19 101 1 19 19 20 28 4 41 -21 16 -51 20 -84 11z"
                    />
                    <path
                      d="M9841 3980 c-25 -5 -49 -14 -54 -19 -26 -26 124 -52 186 -32 33 11
55 37 42 49 -10 10 -128 11 -174 2z"
                    />
                    <path
                      d="M9845 3833 c-63 -34 -50 -63 29 -63 61 0 99 21 94 53 -2 18 -10 22
-48 24 -27 2 -57 -4 -75 -14z"
                    />
                    <path
                      d="M9835 3674 c-32 -17 -46 -34 -39 -46 13 -20 150 0 165 24 22 34 -71
51 -126 22z"
                    />
                    <path
                      d="M9868 3564 c-40 -21 -36 -44 7 -44 36 0 81 32 70 50 -9 14 -46 11
-77 -6z"
                    />
                    <path
                      d="M6666 3658 c-9 -34 -7 -221 3 -236 27 -44 56 134 34 217 -11 42 -28
50 -37 19z"
                    />
                    <path
                      d="M7697 3828 c-40 -30 11 -72 101 -84 84 -11 232 22 232 52 0 14 -28
20 -140 29 -41 3 -97 8 -125 11 -34 4 -56 1 -68 -8z"
                    />
                    <path
                      d="M7741 3657 c-43 -15 -49 -32 -19 -49 27 -15 250 -14 292 1 29 12 36
45 10 55 -28 11 -248 5 -283 -7z"
                    />
                    <path
                      d="M7781 3491 c-52 -14 -28 -48 53 -76 60 -20 111 -13 152 20 40 33 25
43 -77 55 -93 11 -94 11 -128 1z"
                    />
                    <path
                      d="M7797 3343 c-24 -23 16 -53 72 -53 54 0 87 19 78 44 -5 13 -21 16
-75 16 -38 0 -72 -3 -75 -7z"
                    />
                    <path
                      d="M10434 3387 c-54 -21 -78 -42 -71 -61 6 -16 14 -17 55 -11 62 9 111
30 119 51 12 32 -43 44 -103 21z"
                    />
                    <path
                      d="M10409 3217 c-40 -27 -50 -62 -19 -72 50 -16 145 49 119 81 -17 21
-62 17 -100 -9z"
                    />
                    <path
                      d="M10428 3039 c-28 -16 -23 -57 8 -65 56 -14 101 41 55 66 -24 12 -41
12 -63 -1z"
                    />
                    <path
                      d="M2554 3395 c-17 -43 41 -76 142 -83 108 -7 173 22 130 57 -40 33
-261 54 -272 26z"
                    />
                    <path
                      d="M2557 3254 c-11 -11 -8 -49 5 -62 17 -17 130 -15 154 2 12 10 16 18
9 24 -22 21 -155 49 -168 36z"
                    />
                    <path
                      d="M2530 3047 c-41 -21 -32 -50 20 -64 96 -25 182 -9 167 30 -6 17 -94
47 -136 47 -14 0 -37 -6 -51 -13z"
                    />
                    <path
                      d="M2610 2867 c-66 -27 -75 -53 -18 -51 40 1 108 40 108 62 0 18 -27 14
-90 -11z"
                    />
                    <path
                      d="M5588 3268 c-10 -17 -14 -98 -16 -303 -3 -292 2 -340 36 -333 14 3
16 13 14 58 -2 30 5 110 14 177 15 105 16 142 6 256 -14 171 -24 197 -54 145z"
                    />
                    <path
                      d="M4594 3206 c-14 -37 38 -75 141 -102 104 -28 203 -11 157 28 -23 20
-61 33 -157 57 -139 33 -135 33 -141 17z"
                    />
                    <path
                      d="M6445 3191 c-2 -5 -7 -53 -10 -107 -6 -103 3 -193 20 -197 24 -5 53
213 36 266 -12 35 -35 54 -46 38z"
                    />
                    <path
                      d="M6578 3070 c12 -10 46 -26 75 -35 28 -10 108 -40 176 -67 69 -27 142
-49 165 -50 60 -2 142 12 163 27 17 13 16 14 -7 19 -14 2 -49 7 -78 10 -29 3
-98 21 -155 40 -131 45 -260 76 -317 76 l-45 -1 23 -19z"
                    />
                    <path
                      d="M4525 3051 c-19 -32 65 -73 183 -89 99 -14 146 2 99 32 -67 45 -265
84 -282 57z"
                    />
                    <path
                      d="M6656 2907 c-29 -22 -5 -52 61 -77 174 -66 391 -97 451 -66 42 21 72
52 72 71 0 19 -37 19 -70 0 -34 -19 -60 -18 -138 4 -37 11 -123 31 -192 46
-69 14 -134 29 -145 31 -11 3 -28 -1 -39 -9z"
                    />
                    <path
                      d="M4534 2861 c16 -26 73 -44 141 -45 113 -1 176 18 128 39 -31 14 -277
19 -269 6z"
                    />
                    <path
                      d="M6567 2711 c-16 -6 -17 -9 -5 -19 24 -20 200 -74 258 -78 84 -7 136
28 88 60 -38 25 -301 53 -341 37z"
                    />
                    <path
                      d="M6638 2589 c-22 -12 -23 -52 -4 -68 18 -15 203 -54 216 -46 16 10 11
41 -8 58 -28 26 -125 67 -157 67 -16 -1 -38 -5 -47 -11z"
                    />
                    <path
                      d="M8925 2969 c-192 -16 -261 -50 -129 -64 133 -15 304 13 304 48 0 15
-32 28 -60 25 -8 0 -60 -4 -115 -9z"
                    />
                    <path
                      d="M8825 2759 c-51 -10 -85 -32 -71 -46 14 -14 308 -9 334 5 26 15 28
27 6 36 -31 12 -219 15 -269 5z"
                    />
                    <path
                      d="M9571 2706 c-9 -10 -9 -16 -1 -21 13 -8 42 14 34 26 -8 13 -20 11
-33 -5z"
                    />
                    <path
                      d="M8802 2569 c-13 -5 -22 -15 -20 -21 7 -20 197 -23 223 -4 18 13 17
14 -20 25 -48 13 -151 13 -183 0z"
                    />
                    <path
                      d="M8802 2464 c-32 -22 -30 -72 2 -80 14 -3 34 -3 44 1 25 7 72 44 72
55 0 6 -81 40 -95 40 0 0 -11 -7 -23 -16z"
                    />
                    <path
                      d="M10815 2700 c-3 -5 4 -19 16 -31 17 -17 28 -20 57 -14 77 13 90 25
53 45 -25 13 -118 13 -126 0z"
                    />
                    <path
                      d="M10857 2534 c-50 -15 -70 -40 -46 -55 16 -10 119 -12 143 -3 17 6 21
31 8 51 -12 20 -55 23 -105 7z"
                    />
                    <path
                      d="M10838 2409 c-25 -14 -22 -32 8 -69 20 -23 25 -38 21 -60 -8 -35 3
-43 37 -31 54 21 54 141 -1 162 -30 11 -43 11 -65 -2z"
                    />
                    <path
                      d="M1532 2554 c-52 -36 -8 -63 118 -71 65 -4 89 -2 104 10 19 14 19 15
-8 32 -28 19 -124 45 -166 45 -14 0 -36 -7 -48 -16z"
                    />
                    <path
                      d="M1546 2397 c-41 -23 -58 -47 -34 -47 8 0 52 -18 98 -40 92 -45 123
-48 128 -12 3 17 -10 37 -46 73 -57 57 -84 62 -146 26z"
                    />
                    <path
                      d="M1564 2224 c-27 -6 -58 -39 -50 -53 4 -5 41 -17 83 -27 l77 -17 18
23 c23 29 23 47 -2 60 -23 12 -96 20 -126 14z"
                    />
                    <path
                      d="M9845 2274 c-46 -16 -103 -59 -93 -69 11 -11 157 17 170 32 8 11 9
19 1 31 -14 22 -30 24 -78 6z"
                    />
                    <path
                      d="M9843 2093 c-24 -5 -73 -46 -73 -63 0 -22 161 -2 176 22 3 5 -2 18
-12 29 -16 18 -42 22 -91 12z"
                    />
                    <path
                      d="M9794 1906 c-28 -13 -44 -33 -44 -57 0 -5 26 -9 58 -9 56 0 102 15
109 35 11 35 -69 56 -123 31z"
                    />
                    <path
                      d="M9834 1751 c-76 -48 -95 -94 -26 -66 43 18 72 43 72 61 0 22 -15 24
-46 5z"
                    />
                    <path
                      d="M3667 2283 c-12 -3 -17 -12 -15 -26 7 -50 208 -105 225 -62 9 26 -37
59 -107 79 -57 16 -71 17 -103 9z"
                    />
                    <path
                      d="M3640 2083 c0 -38 52 -68 147 -83 59 -10 93 -4 93 17 0 21 -126 76
-187 81 -48 4 -53 2 -53 -15z"
                    />
                    <path
                      d="M3785 1937 c-45 -15 -88 -48 -77 -59 5 -5 31 -8 57 -6 56 4 85 32 62
60 -10 13 -18 14 -42 5z"
                    />
                    <path
                      d="M2940 2188 c0 -121 31 -421 46 -437 11 -12 15 -10 24 13 30 79 -21
496 -61 496 -5 0 -9 -33 -9 -72z"
                    />
                    <path
                      d="M6456 2282 c-9 -15 -28 -568 -21 -632 8 -72 32 -110 72 -110 28 0 28
12 6 95 -12 45 -16 127 -20 335 -2 151 -6 285 -9 298 -4 22 -18 29 -28 14z"
                    />
                    <path
                      d="M5646 2233 c-25 -25 26 -53 179 -97 103 -30 124 -32 151 -11 16 13
17 16 4 25 -24 15 -159 58 -225 70 -33 6 -69 13 -81 15 -12 3 -24 2 -28 -2z"
                    />
                    <path
                      d="M5604 2050 c-51 -20 -31 -60 30 -60 17 -1 73 -9 125 -19 93 -19 141
-16 141 9 0 17 -82 50 -166 65 -85 17 -100 17 -130 5z"
                    />
                    <path
                      d="M7617 2023 c-11 -18 16 -32 98 -53 95 -24 158 -26 175 -5 17 20 6 30
-45 44 -59 17 -221 26 -228 14z"
                    />
                    <path
                      d="M7642 1892 c-13 -4 -24 -11 -25 -17 -5 -28 181 -73 249 -60 76 14 16
58 -102 75 -77 11 -89 11 -122 2z"
                    />
                    <path
                      d="M5685 1874 c-51 -19 -67 -34 -34 -34 25 0 161 -32 192 -45 28 -12 24
59 -5 79 -28 20 -99 20 -153 0z"
                    />
                    <path
                      d="M7661 1727 c-61 -31 -6 -56 144 -64 99 -5 115 -4 115 9 0 22 -53 45
-130 57 -83 14 -100 13 -129 -2z"
                    />
                    <path
                      d="M5676 1701 c-16 -26 128 -80 180 -67 33 9 28 36 -11 55 -38 18 -160
27 -169 12z"
                    />
                    <path
                      d="M7675 1519 c-11 -17 27 -46 77 -59 53 -15 123 -9 123 9 0 29 -185 75
-200 50z"
                    />
                    <path
                      d="M6533 1070 c-34 -14 -28 -36 16 -53 55 -23 246 -61 265 -54 21 8 31
54 16 71 -23 28 -252 56 -297 36z"
                    />
                    <path
                      d="M6576 901 c-22 -3 -41 -11 -43 -17 -8 -26 194 -78 260 -67 38 6 73
35 63 51 -14 23 -202 45 -280 33z"
                    />
                    <path
                      d="M6593 753 c-62 -12 -43 -49 40 -77 82 -28 147 -17 147 25 0 39 -104
68 -187 52z"
                    />
                    <path
                      d="M6641 622 c-38 -12 -35 -28 8 -45 30 -12 47 -14 75 -6 38 11 53 26
39 40 -12 12 -93 20 -122 11z"
                    />
                    <path
                      d="M10490 1749 c-33 -13 -71 -50 -64 -61 4 -5 35 -7 73 -3 99 9 111 13
111 35 0 36 -64 51 -120 29z"
                    />
                    <path
                      d="M10495 1571 c-43 -20 -93 -67 -82 -77 17 -18 210 56 211 81 1 18 -87
16 -129 -4z"
                    />
                    <path
                      d="M10455 1345 c-14 -13 -25 -31 -25 -40 0 -19 34 -20 50 0 7 8 25 15
40 15 30 0 45 21 27 39 -19 19 -66 12 -92 -14z"
                    />
                    <path
                      d="M10522 1209 c-41 -16 -112 -70 -112 -84 0 -36 102 -6 139 40 32 42
19 63 -27 44z"
                    />
                    <path
                      d="M797 1601 c-11 -44 58 -111 114 -111 49 0 33 41 -38 95 -52 40 -69
43 -76 16z"
                    />
                    <path
                      d="M1464 1553 c-30 -45 -25 -228 10 -358 36 -131 45 -149 79 -153 32 -4
35 13 13 76 -27 75 -49 217 -52 332 -2 63 -4 118 -4 123 0 17 -29 5 -46 -20z"
                    />
                    <path
                      d="M803 1409 c-8 -8 -8 -20 -2 -39 9 -23 17 -28 56 -34 63 -8 123 -8
123 2 0 4 -25 24 -56 45 -57 37 -100 47 -121 26z"
                    />
                    <path
                      d="M834 1195 c-5 -11 2 -26 20 -43 23 -23 35 -27 89 -27 54 0 62 2 62
19 0 31 -48 59 -109 64 -46 4 -57 1 -62 -13z"
                    />
                    <path
                      d="M945 1047 c-13 -10 -16 -21 -12 -39 12 -46 61 -46 78 0 17 44 -25 68
-66 39z"
                    />
                    <path
                      d="M2610 1277 c-19 -7 -35 -16 -34 -22 2 -24 67 -47 147 -52 99 -6 121
5 109 52 l-8 30 -90 2 c-50 1 -104 -3 -124 -10z"
                    />
                    <path
                      d="M4550 805 c-33 -16 -34 -17 -16 -31 11 -8 32 -14 47 -14 15 0 59 -13
97 -30 39 -16 84 -30 100 -30 33 0 78 41 69 64 -15 39 -234 69 -297 41z"
                    />
                    <path
                      d="M9395 1010 c-22 -10 -56 -22 -76 -25 -23 -3 -54 -21 -83 -46 -82 -73
-55 -91 82 -54 88 24 165 55 176 73 7 12 -31 72 -46 71 -7 0 -31 -9 -53 -19z"
                    />
                    <path
                      d="M8729 883 c-13 -36 6 -57 27 -29 8 11 14 28 14 38 0 28 -29 21 -41
-9z"
                    />
                    <path
                      d="M9323 799 c-122 -14 -177 -50 -121 -80 25 -13 163 -7 220 11 27 8 40
18 44 36 4 14 5 26 3 28 -12 10 -83 12 -146 5z"
                    />
                    <path
                      d="M9211 592 c-44 -22 -60 -47 -43 -64 17 -17 171 -13 217 6 46 20 44
40 -5 60 -53 21 -126 20 -169 -2z"
                    />
                    <path
                      d="M9323 466 c-62 -29 -143 -101 -143 -127 0 -24 3 -24 48 5 20 14 69
37 107 52 39 15 79 35 90 46 20 19 20 19 1 34 -27 19 -43 18 -103 -10z"
                    />
                    <path
                      d="M4440 8250 c0 -12 150 -60 213 -67 42 -5 47 -4 47 14 0 25 -32 39
-120 53 -82 13 -140 13 -140 0z"
                    />
                    <path
                      d="M10096 7812 c-9 -16 18 -22 156 -34 98 -9 131 -9 140 0 9 9 9 15 0
24 -16 16 -286 25 -296 10z"
                    />
                    <path
                      d="M673 6393 c-12 -32 -24 -76 -27 -99 -6 -46 7 -244 29 -435 15 -132
21 -159 36 -159 25 0 28 57 14 252 -8 112 -18 270 -22 351 l-8 148 -22 -58z"
                    />
                    <path
                      d="M1371 6077 c-8 -11 -6 -16 12 -25 36 -20 237 -82 262 -82 33 0 55 10
55 25 0 52 -293 124 -329 82z"
                    />
                    <path
                      d="M1371 5892 c-33 -23 17 -45 150 -66 73 -12 89 -12 122 1 20 8 37 18
37 23 0 22 -282 60 -309 42z"
                    />
                    <path
                      d="M413 5684 c-8 -22 27 -66 71 -88 30 -15 43 -16 62 -7 30 13 30 24 2
51 -48 45 -125 71 -135 44z"
                    />
                    <path
                      d="M628 3529 c-26 -14 -22 -36 10 -69 33 -33 101 -47 145 -31 40 16 29
33 -34 54 -29 10 -62 27 -72 38 -20 22 -25 22 -49 8z"
                    />
                    <path
                      d="M694 3356 c-68 -30 -52 -58 42 -76 75 -14 90 -8 75 32 -11 29 -47 58
-71 58 -8 -1 -29 -7 -46 -14z"
                    />
                    <path
                      d="M732 3160 c-56 -23 5 -89 69 -75 38 9 47 31 23 55 -24 24 -62 32 -92
20z"
                    />
                    <path
                      d="M715 2996 c-30 -22 -9 -50 41 -54 43 -3 108 30 101 51 -6 19 -118 21
-142 3z"
                    />
                    <path
                      d="M11448 2554 c-9 -20 -9 -27 2 -34 27 -17 56 13 44 45 -9 23 -33 18
-46 -11z"
                    />
                  </g>
                </svg>

                {totalItems > 0 && (
                  <span className="cart-badge">{totalItems}</span>
                )}
              </button>

              <Button
                variant="outline"
                className="btn-mon-compte"
                onClick={() => goTo("compte")}
                disabled={authStatus === "loading"}
              >
                {isLoggedIn
                  ? userProfil?.prenom?.toUpperCase() || t?.myAccount
                  : t?.login || "LOGIN"}
              </Button>

              <button
                type="button"
                className={`btn-mobile-menu ${
                  isMobileMenuOpen ? "is-open" : ""
                }`}
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                aria-label={
                  isMobileMenuOpen
                    ? t?.closeMenu || "Fermer le menu"
                    : t?.menu || "Menu"
                }
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-navigation-drawer"
                title={
                  isMobileMenuOpen
                    ? t?.closeMenu || "Fermer le menu"
                    : t?.menu || "Menu"
                }
                style={{
                  fontSize: "34px",
                  fontWeight: "700",
                  lineHeight: 1,
                  width: "44px",
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isMobileMenuOpen ? "✕" : "☰"}
              </button>
            </div>
          </div>
        </nav>
        <div
          id="mobile-navigation-drawer"
          className={`mobile-drawer ${isMobileMenuOpen ? "open" : ""}`}
          aria-hidden={!isMobileMenuOpen}
        >
          <div
            className="mobile-drawer-backdrop"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          <div className="mobile-drawer-panel">
            <div className="mobile-drawer-head">
              <span className="mobile-drawer-title">
                {t?.menu || "Navigation"}
              </span>

              <button
                type="button"
                className="mobile-drawer-close"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="mobile-drawer-links">
              <button onClick={() => goTo("compte")}>
                {t?.myAccount || "Mon Compte"}
              </button>

              <button onClick={() => goTo("panier")}>
                {t?.myCart || "MON PANIER"}
              </button>

              <button onClick={() => goTo("magasin")}>
                {t?.onlineShop || "Magasin"}
              </button>

              <button onClick={() => goTo("actions")}>
                {t?.actions || "Actions"}
              </button>

              {(isAdmin || isCollaborator) && (
                <button onClick={() => goTo("dashboard-magasin")}>
                  {t?.dashboardOrders || "Commandes"}
                </button>
              )}

              {isAdmin && (
                <button onClick={() => goTo("admin")}>
                  {t?.adminInventory || "Inventaire"}
                </button>
              )}
              {isAdmin && (
                <button onClick={() => goTo("admin-milk-types")}>
                  {t?.adminMilkTypes || "Types de lait"}
                </button>
              )}
            </div>
          </div>
        </div>
        <div
          className={`bottom-nav bottom-nav-overlay ${
            showFloatingNav ? "is-visible" : "is-hidden"
          }`}
          role="navigation"
          aria-label={t?.mainNavigation || "Navigation principale"}
        >
          <button
            type="button"
            className={isTabActive("accueil") ? "active" : ""}
            onClick={() => goTo("accueil")}
          >
            <span className="bn-label">ACCUEIL</span>
          </button>

          <button
            type="button"
            className={isTabActive("magasin") ? "active" : ""}
            onClick={() => goTo("magasin")}
          >
            <span className="bn-label">MAGASIN</span>
          </button>

          <button
            type="button"
            className={isTabActive("actions") ? "active" : ""}
            onClick={() => goTo("actions")}
          >
            <span className="bn-label">NOS ACTIONS</span>
          </button>

          <button
            type="button"
            className={isTabActive("panier") ? "active" : ""}
            onClick={() => goTo("panier")}
          >
            <span className="bn-label">{t?.cartNav || "PANIER"}</span>
            {totalItems > 0 && (
              <span className="bottom-badge">{totalItems}</span>
            )}
          </button>

          <button
            type="button"
            className={isTabActive("compte") ? "active" : ""}
            onClick={() => goTo("compte")}
          >
            <span className="bn-label">MON COMPTE</span>
          </button>
        </div>
        <style jsx="true">{`
          /* --------------------------------------------------------------
     Logo principal
     --------------------------------------------------------------
     Ce bloc garde le logo aligné proprement dans la navigation
     et évite qu’il se compresse quand l’espace horizontal réduit.
  -------------------------------------------------------------- */
          .nav-logo {
            display: inline-flex;
            align-items: center;
            justify-content: flex-start;
            flex-shrink: 0;
            text-decoration: none;
            color: #c4a56e;
            line-height: 1;
            overflow: visible;
          }

          /* --------------------------------------------------------------
     SVG du logo
     --------------------------------------------------------------
     Ce bloc contrôle la largeur par défaut du logo et conserve
     son ratio sans limitation automatique parasite.
  -------------------------------------------------------------- */
          .nav-logo-svg {
            display: block;
            width: 170px;
            min-width: 170px;
            height: auto;
            max-width: none;
            object-fit: contain;
          }

          /* --------------------------------------------------------------
     Focus clavier du bouton menu mobile
     --------------------------------------------------------------
     Ce bloc améliore l’accessibilité clavier du bouton burger
     avec un contour visible lors de la navigation au clavier.
  -------------------------------------------------------------- */
          .btn-mobile-menu:focus-visible {
            outline: 2px solid currentColor;
            outline-offset: 2px;
          }

          /* --------------------------------------------------------------
     Bottom navigation overlay
     --------------------------------------------------------------
     Ce bloc définit la navigation flottante basse, mais elle
     reste masquée par défaut tant qu’un media query ne l’active pas.
  -------------------------------------------------------------- */
          .bottom-nav-overlay {
            position: fixed;
            left: 10px;
            right: 10px;
            bottom: max(8px, env(safe-area-inset-bottom));
            z-index: 1200;

            display: none;
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: 2px;

            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;

            pointer-events: auto;
            transition: opacity 0.22s ease, transform 0.22s ease;
          }

          /* --------------------------------------------------------------
     État visible de la bottom nav
     --------------------------------------------------------------
     Ce bloc rend la navigation basse visible sans décalage vertical.
  -------------------------------------------------------------- */
          .bottom-nav-overlay.is-visible {
            opacity: 1;
            transform: translateY(0);
          }

          /* --------------------------------------------------------------
     État masqué de la bottom nav
     --------------------------------------------------------------
     Ce bloc masque la navigation basse avec une légère translation.
  -------------------------------------------------------------- */
          .bottom-nav-overlay.is-hidden {
            opacity: 0;
            transform: translateY(12px);
          }

          /* --------------------------------------------------------------
     Boutons de la bottom nav
     --------------------------------------------------------------
     Ce bloc uniformise le rendu des boutons de navigation basse
     et conserve une zone tactile suffisante.
  -------------------------------------------------------------- */
          .bottom-nav-overlay button {
            position: relative;
            appearance: none;
            border: none !important;
            outline: none;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;

            min-height: 44px;
            padding: 0 2px;

            display: flex;
            align-items: center;
            justify-content: center;

            cursor: pointer;
            transition: transform 0.18s ease, opacity 0.18s ease;
          }

          /* --------------------------------------------------------------
     États hover et focus des boutons de bottom nav
     --------------------------------------------------------------
     Ce bloc donne un léger relief visuel aux actions interactives.
  -------------------------------------------------------------- */
          .bottom-nav-overlay button:hover,
          .bottom-nav-overlay button:focus-visible {
            transform: translateY(-5px);
            outline: none;
          }

          /* --------------------------------------------------------------
     Onglet actif de la bottom nav
     --------------------------------------------------------------
     Ce bloc garde le même relief visuel pour l’onglet actif.
  -------------------------------------------------------------- */
          .bottom-nav-overlay button.active {
            transform: translateY(-5px);
          }

          /* --------------------------------------------------------------
     Libellés de la bottom nav
     --------------------------------------------------------------
     Ce bloc contrôle la typographie premium des textes de navigation.
  -------------------------------------------------------------- */
          .bottom-nav-overlay .bn-label {
            font-family: "Playfair Display", serif;
            font-size: 13px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            white-space: nowrap;
            color: #c3a133;
            text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000,
              1px 1px 0 #000, 0 1px 8px rgba(0, 0, 0, 0.35);
          }

          /* --------------------------------------------------------------
     Libellé actif de la bottom nav
     --------------------------------------------------------------
     Ce bloc accentue la couleur de l’onglet actif.
  -------------------------------------------------------------- */
          .bottom-nav-overlay button.active .bn-label {
            color: #e0bc52;
          }

          /* --------------------------------------------------------------
     Badge quantité de la bottom nav
     --------------------------------------------------------------
     Ce bloc affiche le compteur panier sur la navigation basse.
  -------------------------------------------------------------- */
          .bottom-nav-overlay .bottom-badge {
            position: absolute;
            top: -2px;
            right: 2px;
            min-width: 18px;
            height: 18px;
            border-radius: 999px;
            padding: 0 5px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #d4af37;
            color: #000;
            font-size: 10px;
            font-weight: 900;
            line-height: 1;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.22);
          }

          /* --------------------------------------------------------------
     Responsive tablette et mobile
     --------------------------------------------------------------
     Ce bloc garde un seul conteneur horizontal pour le logo,
     le panier, le compte et le menu burger.

     La structure reste sur une seule ligne :
     - logo à gauche
     - groupe panier / compte / menu à droite

     Le burger redevient un élément normal du flux et n’entre
     plus en collision avec le reste du header.
  -------------------------------------------------------------- */
          @media (max-width: 768px) {
            .main-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99999;
}

            .nav-container {
              display: grid;
              grid-template-columns: minmax(0, 1fr) auto;
              align-items: center;
              column-gap: 12px;
              padding: 0 14px;
            }

            .nav-logo {
              min-width: 0;
              overflow: visible;
              justify-self: start;
            }

            .nav-logo-svg {
              width: 138px;
              min-width: 138px;
              height: auto;
              max-width: none;
            }

            .nav-links {
              display: grid;
              grid-template-columns: auto auto auto;
              align-items: center;
              justify-content: end;
              column-gap: 10px;
              margin-right: 0;
              padding-right: 0;
            }

            .nav-cart-icon {
              margin: 0;
              transform: translateX(-2px);
            }

            .btn-mon-compte {
              margin: 0;
              font-size: 15px;
              line-height: 1;
              white-space: nowrap;
              transform: translateX(-2px);
            }

            .btn-mobile-menu {
              position: relative;
              top: auto;
              right: auto;
              z-index: auto;
              margin: 0;
              justify-self: end;
            }

            .bottom-nav-overlay {
              display: none !important;
            }
          }

          /* --------------------------------------------------------------
     Responsive mobile compact
     --------------------------------------------------------------
     Ce bloc affine la même ligne unique sur les petits écrans :
     le logo garde sa place, le panier recule un peu à gauche,
     le compte gagne légèrement en taille, et le burger reste
     aligné proprement à droite.
  -------------------------------------------------------------- */
          @media (max-width: 520px) {
            .nav-container {
              column-gap: 10px;
              padding: 0 10px;
            }

            .nav-logo {
              min-width: 0;
              overflow: visible;
            }

            .nav-logo-svg {
              width: 70px;
              min-width: 118px;
              height: auto;
              max-width: none;
            }

            .nav-links {
              column-gap: 8px;
            }

            .nav-cart-svg {
              width: 40px;
              height: 84px;
            }

            .nav-cart-icon {
              transform: translateX(-1px);
            }

            .btn-mon-compte {
              font-size: 16px;
              transform: translateX(-4px);
            }

            .btn-mobile-menu {
              width: 44px;
              height: 44px;
            }
          }
 
        `}</style>{" "}
      </>
    );
  }
);

export default Navigation;
